/* DiFluid OS — content.
   Every word that changes with the role lives here. os.js does the
   painting; this file is the copy deck. Edit it on its own.

   Resolution order for any role-keyed object:
     exact role id  ->  role family  ->  "default"

   Families:
     green  producer, exporter, importer, q
     roast  roaster, home
     bar    cafe, hobby
     teach  educator

   A role id used as a key beats its family, so "q" can say something
   a producer would not. */
window.OS_CONTENT = (function () {
  "use strict";

  var FAMILY = {
    producer: "green",
    exporter: "green",
    importer: "green",
    q: "green",
    roaster: "roast",
    home: "roast",
    cafe: "bar",
    hobby: "bar",
    educator: "teach"
  };

  /* ── Home ───────────────────────────────────────── */

  /* ── The recommended system, by role ─────────────

     Under the role headline: the system we recommend for that reader,
     what is in the box, and the CoffeeOS tools that work with it.
     Written to the DiFluid Voice skill. Every lead says how the
     products in that system work together for that reader, not one
     feature per device.

     Short on purpose. Every instrument and every tool has its own
     page; this only has to make someone want to click. */

  /* In the box: what each device measures, in plain words. */
  var GEAR = {
    "microbalance-ti":   { name: "Microbalance Ti", line: "Brew scale with a 3,000 g capacity. Records dose, yield and the flow of the pour." },
    "r2-extract":        { name: "R2 Extract",      line: "Total dissolved solids in about four seconds. 0 to 30%, ±0.02%." },
    "omni":              { name: "Omni",            line: "Measures the roast color of whole beans and grounds, and the particle size distribution of your grind." },
    "omix-plus":         { name: "Omix Plus",       line: "Moisture, water activity, density, screen size and color, from one sample." },
    "omniflux":          { name: "OmniFlux",        line: "Roast by Color. Measures roast color in real time, alongside your temperature curve." },
    "airwave":           { name: "AirWave",         line: "Plug-and-play smoke removal. Breaks roasting smoke down into carbon dioxide and water vapor." },
    "coffmeter-a1":      { name: "CoffMeter A1",    line: "Measures roast color." },
    "coffmeter-m1":      { name: "CoffMeter M1",    line: "Measures moisture and density." },
    "coffmeter-w1":      { name: "CoffMeter W1",    line: "Measures water activity." },
    "moment":            { name: "Moment",          line: "Taste by Temperature. Shows the flavors to expect as your coffee cools, from a profile written by a roaster, a barista or you." },
    "coaster":           { name: "Particle Analysis Coaster", line: "Spread your grounds on it and use your phone's camera to measure grind size distribution in CoffeeOS." },
    "colorguard":        { name: "ColorGuard",      line: "Dual-point color calibration card for Omni and Omix Plus." },
    "bean-sweeper":      { name: "Bean Sweeper",    line: "Sits on the Omix Plus container and sweeps off the excess, so every sample is the same volume." }
  };

  /* CoffeeOS tools: one plain line on what each does. A role can
     swap in its own line through `toolLines` on its KIT entry, when
     the reason that reader uses the tool is different. */
  var TOOLS = {
    "bean-manager":       { name: "Bean Manager",       line: "Your coffee inventory. Every measurement is attached to the bean it came from." },
    "roast-color":        { name: "Roast Color Analyzer", line: "Measures the color distribution of whole beans and grounds, and compares the two." },
    "particle-analyzer":  { name: "Particle Analyzer",  line: "Measure your grind's particle size distribution and understand what it means for your brew." },
    "brew-recorder":      { name: "Brew Recorder",      line: "Records your brew live from the scale. Each record is linked to the bean in your Bean Manager inventory." },
    "brew-controller":    { name: "Brew Controller",    line: "Plots strength against extraction, so you can see how a brew compares with your target and what to change." },
    "brew-ratio":         { name: "Brew Ratio Calculator", line: "Works out dose, water or ratio from the other two." },
    "grinder-converter":  { name: "Grinder Converter",  line: "Tells you what setting to use on another grinder to get a similar grind." },
    "grinder-manager":    { name: "Grinder Manager",    line: "Build a profile curve for your grinder from measured grind sizes, so you know which setting to use." },
    "notes-manager":      { name: "Notes Manager",      line: "All your notes on every test and session, organized by Tool and searchable." },
    "flavor-thermometer": { name: "Flavor Thermometer", line: "Create Moment profiles of the flavors to expect at each temperature, follow someone else's, and share your own." }
  };

  /* The system each role is shown. `h` is its key in the catalog the
     homepage section writes. */
  var KIT = {
    producer: {
      h: "bean-analysis",
      lead:
        "The Coffee Bean Analysis System is an affordable way to get precise measurements " +
        "you can share with your buyers. Measure moisture, density and water activity on " +
        "the green coffee, and roast color and grind size on a sample roast. Every reading " +
        "is stored in CoffeeOS, where your buyers can see the data behind the coffee for themselves.",
      gear: ["coffmeter-w1", "coffmeter-m1", "coffmeter-a1", "coaster"],
      tools: ["bean-manager", "roast-color", "particle-analyzer", "notes-manager"],
      toolLines: {
        "bean-manager": "Your lot inventory. Every reading is attached to the lot, ready to share with buyers."
      }
    },
    exporter: {
      h: "bean-analysis",
      lead:
        "Measure moisture, density and water activity at origin, and roast color and grind " +
        "size on a sample roast. When your buyer measures with the same devices on " +
        "arrival, the two sets of readings can be compared directly. Share the results from " +
        "CoffeeOS so both sides are looking at the same data.",
      gear: ["coffmeter-w1", "coffmeter-m1", "coffmeter-a1", "coaster"],
      tools: ["bean-manager", "roast-color", "particle-analyzer", "notes-manager"],
      toolLines: {
        "bean-manager": "Your lot inventory. Every reading is attached to the lot, ready to share with buyers."
      }
    },
    importer: {
      h: "roast-validation",
      lead:
        "Measure moisture, density and water activity as soon as the coffee arrives, to " +
        "confirm it matches what you ordered, then use those measurements to plan the roast. " +
        "Afterward, check the roast color, brew the coffee with Microbalance Ti and " +
        "R2 Extract, and save the recipe that works best, so you can validate the roast you " +
        "were aiming for.",
      gear: ["coffmeter-a1", "coffmeter-m1", "coffmeter-w1", "microbalance-ti", "r2-extract", "coaster"],
      tools: ["bean-manager", "roast-color", "brew-controller", "particle-analyzer", "notes-manager"],
      toolLines: {
        "brew-controller": "Plot several brews on one chart to validate your roast and find the best recipe."
      }
    },
    q: {
      h: "roast-validation-ultimate",
      lead:
        "Omix Plus measures moisture, water activity, density, screen size and roast color " +
        "from one sample. For water activity it uses a chilled mirror, cooling a small mirror " +
        "until dew forms on it, which is one of the most precise ways to measure it. Use the " +
        "Particle Analysis Coaster to measure grind size distribution, R2 Extract to measure " +
        "extraction, and Microbalance Ti to record the brew. Every result is linked to the " +
        "bean in CoffeeOS, so any figure you quote has hard data behind it.",
      gear: ["omix-plus", "microbalance-ti", "r2-extract", "bean-sweeper", "coaster"],
      tools: ["bean-manager", "roast-color", "particle-analyzer", "brew-controller", "notes-manager"]
    },
    roaster: {
      h: "roast-control-ultimate",
      lead:
        "Before you roast, Omix Plus measures density, moisture and screen size, which help " +
        "you choose a charge temperature. During the roast, OmniFlux records roast color in " +
        "real time beside your temperature curve, so you can roast by color. Temperature is " +
        "measured differently from one roaster to the next, which makes temperature curves " +
        "hard to match. Color curves are much easier to match, so you can reproduce a sample " +
        "roast on your production roaster. After the drop, measure whole beans and grounds " +
        "with Omix Plus to confirm the roast delta you wanted. AirWave removes the roasting smoke.",
      gear: ["omix-plus", "omniflux", "airwave"],
      tools: ["bean-manager", "roast-color", "notes-manager"]
    },
    home: {
      h: "roastery-starter",
      lead:
        "CoffMeter A1 tells you the roast color of your beans. CoffMeter M1 measures the " +
        "moisture and density of the green coffee, which help you choose a charge temperature " +
        "before the batch goes in. Both are linked to the bean in CoffeeOS, so you can compare " +
        "this week's roast with last week's and see what to adjust.",
      gear: ["coffmeter-a1", "coffmeter-m1"],
      tools: ["bean-manager", "roast-color", "notes-manager"]
    },
    cafe: {
      h: "brew-control-ti",
      lead:
        "Microbalance Ti records dose, yield and flow rate as you brew, and R2 Extract " +
        "measures the strength of the cup. In the Brew Recorder in CoffeeOS, a brew " +
        "you like becomes a recipe anyone can follow step by step, so the next barista can " +
        "follow your example. Train new staff on your recipes in the app, and record " +
        "everyone's brews to check they're consistent.",
      gear: ["microbalance-ti", "r2-extract"],
      tools: ["brew-recorder", "brew-controller", "brew-ratio", "bean-manager", "notes-manager"]
    },
    hobby: {
      h: "sensory-bcs",
      lead:
        "Spread your grounds on the Particle Analysis Coaster and use the Particle Analyzer " +
        "in CoffeeOS to see your grind size distribution. The Grinder Converter then " +
        "tells you what setting to use on another grinder to get a similar grind. " +
        "Microbalance Ti and R2 Extract help you understand and repeat your brewing process. " +
        "Moment helps you taste: follow a roaster's profile to know which flavors to expect " +
        "as the cup cools, or build your own and see whether you taste the same thing next time.",
      gear: ["microbalance-ti", "r2-extract", "moment", "coaster"],
      tools: ["particle-analyzer", "grinder-converter", "brew-recorder", "brew-controller", "flavor-thermometer", "notes-manager"]
    },
    educator: {
      h: "bcs-ultimate-ti",
      lead:
        "Omni measures grind size distribution and roast color, Microbalance Ti records dose, " +
        "yield and the flow of the pour, and R2 Extract measures extraction. Students record " +
        "each brew in CoffeeOS and share the results with you in the app, so you can see " +
        "exactly how each of them brewed.",
      gear: ["omni", "microbalance-ti", "r2-extract"],
      tools: ["particle-analyzer", "brew-recorder", "brew-controller", "grinder-manager", "notes-manager"]
    }
  };

  var HOME = {
    head: {
      producer: {
        h: "Moisture and water activity, before the lot ships.",
        n: "Measure them at the mill and send the results with your offer."
      },
      exporter: {
        h: "The same reading at origin and on arrival.",
        n: "Measure the lot before it ships and again when it arrives, so any difference you find comes from the coffee, not the method."
      },
      importer: {
        h: "Confirm the coffee you received.",
        n: "Check it against what the seller told you."
      },
      q: {
        h: "Color, moisture and size, the attributes CVA describes.",
        n: "Measured precisely, and linked to the beans they came from."
      },
      roaster: {
        h: "Match your production roast to the sample.",
        n: "Roast by color with a live color curve, and remove the smoke with AirWave."
      },
      cafe: {
        h: "Make the cup you already like, again.",
        n: "Record your best recipe so anyone on your team can brew it the same way."
      },
      hobby: {
        h: "Know why today's cup tasted better than yesterday's.",
        n: "Measure the grind, the pour and the extraction, then taste it at the right temperature."
      },
      home: {
        h: "Roast the next batch better than the last.",
        n: "Measure roast color, moisture and density, so you can see what changed between batches."
      },
      educator: {
        h: "See how each student brewed, not just the cup.",
        n: "Students record their work and share it with you in CoffeeOS, on hardware they can buy themselves."
      }
    },

    /* Lines the homepage section shipped with before this rewrite.
       The theme editor's Role text blocks were filled with these, and
       an editor field always beats this file, so without this list the
       new copy would never show. A field still holding one of these
       exact lines is treated as untouched and ignored. Anything that
       was actually edited in the editor still wins. */
    retired: [
      "Two numbers, recorded before it leaves the mill.",
      "Written down at the mill, not estimated a fortnight later.",
      "One method, both ends of the shipment.",
      "Color, moisture, size — the physicals CVA already asks for.",
      "Recorded, not estimated.",
      "Ground color you can repeat.",
      "Five grams, several tests, the average kept.",
      "Yesterday's brew, written down.",
      "Setting 40 here is not setting 40 there.",
      "Measure the grounds, then convert the setting.",
      "Roast color without a lab bench.",
      "A reading you can hold against last week's.",
      "Hardware a student can buy. Work that produces a file.",
      "Every session ends in a file.",
      "Every practice run measured, so what you take on stage is a recipe rather than a good day."
    ],

    gear: GEAR,
    tools: TOOLS,
    kit: KIT
  };

  /* ── Stage pages ────────────────────────────────── */

  var STAGES = {

    /* ═══════════════ FARM & GREEN ═══════════════
       System-first layout, painted by assets/os-room.js. What is
       particular to this page: the lot is the thing. The flow ends
       on the lot's own page in CoffeeOS with every green reading on
       it, and the sharing block is about sending that page to a
       buyer. R2 PP opens the page at the cherry. */
    farm: {
      slug: "farm",
      eyebrow: "Farm & green",
      title: "Farm & green",
      hero: "os-farm-hero",
      heroAlt: "Omix Plus at a drying patio, parchment under a tunnel",

      head: {
        producer: {
          h: "Know the lot is dry.",
          n: "Measure moisture and water activity on the drying bed, and send the results with the lot."
        },
        exporter: {
          h: "The same reading at both ends.",
          n: "The same devices at origin and on arrival, so any difference you find comes from the coffee, not the method."
        },
        importer: {
          h: "Check what you were told.",
          n: "Moisture, water activity and density on arrival, before the lot goes into a contract, saved to the lot."
        },
        q: {
          h: "Measured, not estimated.",
          n: "Moisture, water activity, density, screen size and color distribution, the attributes CVA describes, each linked to the coffee it was measured on."
        },
        roaster: {
          h: "Know what you bought.",
          n: "Density and moisture help you set the charge temperature. Guessing them costs a batch."
        },
        cafe: {
          h: "Why the espresso keeps drifting.",
          n: "When the moisture or density of the green changes between bags, the shot changes with it. Ask your roaster for the readings."
        },
        hobby: {
          h: "Why bags taste different.",
          n: "Moisture and density are most of why the same recipe drifts between bags."
        },
        home: {
          h: "Roast the same lot twice.",
          n: "Density and moisture help you set the charge temperature. Measure both and save them to the lot in CoffeeOS."
        },
        educator: {
          h: "Green grading students can do.",
          n: "Devices a student can afford, and results they can share with you in CoffeeOS."
        }
      },

      intro: "",

      system: {
        eyebrow: "The system for you",
        cta: "See what is in it",
        green: {
          h: "roaster-barista-system-coffmeter-w1-m1-a1", name: "Coffee Bean Analysis System",
          lead: "CoffMeter M1 measures moisture and density in two seconds, CoffMeter W1 measures water activity, and CoffMeter A1 measures the color of the sample roast. Each does one job, which keeps them affordable for a mill. Save every reading to the lot in CoffeeOS."
        },
        q: {
          h: "pro-roaster-barista-bundle-omix-r2-microbalance-ti", name: "Roast Validation System Ultimate",
          lead: "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample, with the result of every test, not only the average. Brew the sample on a fixed recipe with Microbalance Ti, measure the extraction with R2 Extract, and link the green, the roast and the cup to the same coffee in CoffeeOS."
        },
        roast: {
          h: "roast-control-system-omix-plus-airwave", name: "Roast Control System Green",
          lead: "Omix Plus measures the green before the charge, so the density and moisture you roast with are measured, not quoted. AirWave takes the smoke out of the room. Save every reading to the lot in CoffeeOS, and link each roast back to it."
        },
        home: {
          h: "roastery-starter-kit-coffmeter-a1-m1", name: "Roastery Starter Kit",
          lead: "CoffMeter M1 measures the moisture and density of the green in two seconds, which help you set a charge temperature. CoffMeter A1 measures the roast color after the drop. Save both to the lot in CoffeeOS."
        },
        bar: {
          h: "roastery-starter-kit-coffmeter-a1-m1", name: "Roastery Starter Kit",
          lead: "CoffMeter M1 measures the moisture and density of the beans you were sent, and CoffMeter A1 their roast color. When a shot drifts, you can see whether the beans changed before you touch the grinder."
        },
        teach: {
          h: "roaster-barista-system-coffmeter-w1-m1-a1", name: "Coffee Bean Analysis System",
          lead: "Each device does one job, so three students can measure at once: CoffMeter M1 for moisture and density, W1 for water activity, A1 for roast color. Students save every reading and share it with you in CoffeeOS."
        },
        comp: {
          h: "roaster-barista-system-coffmeter-w1-m1-a1", name: "Coffee Bean Analysis System",
          lead: "Moisture, water activity and density on a competition coffee, taken by you before you buy the only bag of it you will get, and saved to the lot so you can link every practice roast and brew to it."
        }
      },


      /* Where each reading happens, in the order the coffee meets it. */
      chain: {
        h: "Measured where it happens.",
        n: {
          green: "The cherry at the tree, the parchment on the drying bed, the green in the warehouse, the sample roast in the lab. Each reading is taken where it happens and saved to the same lot.",
          q: "Each figure is measured where it happens, with the device built for it, and saved to the same lot.",
          roast: "By the time a lot reaches you it may have been measured at every step. Ask your supplier to share the lot in CoffeeOS and you can see those readings.",
          bar: "The bag on your shelf was measured at the tree, on the patio and in the warehouse before it was roasted.",
          teach: "A class can run it as stations: cherry, drying, storage, sample roast. Students save each reading to the same lot.",
          comp: "The lot you compete on may have been measured at the tree, on the patio and in the warehouse. Ask the seller to share it in CoffeeOS.",
          default: "Each reading is taken where it happens and saved to the same lot."
        },
        steps: [
          { gear: "r2-pp",        name: "R2 PP",        place: "At the tree",      reading: "Brix of the cherry" },
          { gear: "coffmeter-m1", name: "CoffMeter M1", place: "On the drying bed", reading: "Moisture and density" },
          { gear: "coffmeter-w1", name: "CoffMeter W1", place: "In the warehouse",  reading: "Water activity" },
          { gear: "coffmeter-a1", name: "CoffMeter A1", place: "At the sample roast", reading: "Roast color" }
        ]
      },

      flow: {
        h: "Every reading is saved to the lot.",
        n: {
          green: "Measure on the patio, in the sample room or on arrival. Results from each device are imported into the lot's record in CoffeeOS, and that record is what you share with your buyer.",
          q: "Each result is imported from the device that measured it into the sample's own record.",
          roast: "Link each roast to the green lot it came from, and you can trace a batch that behaved differently back to its green readings.",
          bar: "The readings behind the bag on your shelf, on the page your roaster can send you.",
          teach: "Each student's readings are saved to the lot's page, so the class compares results in one place.",
          comp: "Everything you know about the competition lot, on one page, before the first practice roast.",
          default: "Results from each device are imported into the lot's record in CoffeeOS."
        },
        cols: ["Measure", "In CoffeeOS", "On the lot"],
        links: [
          { gear: "omix-plus",    name: "Omix Plus",    measure: "Moisture, water activity, density, size, color", tool: "bean-manager", toolName: "Bean Manager", toolLine: "The lot's own page" },
          { gear: "coffmeter-m1", name: "CoffMeter M1", measure: "Moisture and density",   tool: "bean-manager", toolName: "Bean Manager", toolLine: "The lot's own page" },
          { gear: "coffmeter-w1", name: "CoffMeter W1", measure: "Water activity",         tool: "bean-manager", toolName: "Bean Manager", toolLine: "The lot's own page" },
          { gear: "coffmeter-a1", name: "CoffMeter A1", measure: "Color of the sample roast", tool: "roast-color", toolName: "Roast Color Analyzer", toolLine: "Saved to the same lot" }
        ],
        record: {
          img: "os-app-bean-5",
          alt: "A green lot's page in CoffeeOS: China, Menglian, 9.9% moisture, with crop information and quality metrics",
          callouts: [
            { x: 50, y: 22, t: "The lot: China, Menglian, 9.9% moisture" },
            { x: 50, y: 50, t: "Variety, process, altitude" },
            { x: 50, y: 81, t: "Quality metrics, imported from the devices" },
            { x: 50, y: 97, t: "Water activity 0.638, density 825 g/L" }
          ]
        }
      },

      tools: [
        {
          slug: "omix-plus",
          name: "Omix Plus",
          img: "os-farm-omix", pos: "50% 50%",
          alt: "Omix Plus on parchment coffee under a drying tunnel",
          tool: "bean-manager", toolName: "Bean Manager",
          h: "Every green reading from one sample.",
          does: "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample of green, parchment, dried cherry or roasted coffee. Water activity is measured by cooling a mirror until dew forms on it, to ±0.005, in about 30 seconds. Every result is imported into the lot's record in CoffeeOS.",
          spec: "Moisture ±0.1% · water activity ±0.005 · density ±0.5 g/L",
          shot: "os-app-bean-4",
          shotAlt: "Bean qualities in CoffeeOS imported from Omix Plus: moisture 9.9%, water activity 0.638, bulk density 825.1 g/L, screen size 19 to 21",
          callouts: [
            { x: 50, y: 38, t: "Moisture 9.9%" },
            { x: 50, y: 44, t: "Water activity 0.638" },
            { x: 50, y: 50, t: "Bulk density 825.1 g/L" },
            { x: 50, y: 62, t: "Screen size 19 to 21" }
          ],
          why: {
            green: "Moisture, water activity, density, screen size and color from one sample, at the patio or on arrival.",
            q: "Water activity by the dew point method, so the figure holds up in a report.",
            roast: "Density and moisture off one sample, before you pick a charge temperature.",
            bar: "The device your roaster uses to check green coffee. Ask for the readings when you ask for the beans.",
            hobby: "The device behind the moisture and density on a green spec sheet.",
            teach: "One device covers most of a green grading module.",
            comp: "Moisture, water activity, density, size and color from one sample, so the coffee you compete on is described by your own measurements."
          }
        },
        {
          slug: "coffmeter-m1",
          name: "CoffMeter M1",
          img: "os-farm-m1", pos: "50% 50%",
          alt: "CoffMeter M1 measuring a dish of green coffee",
          tool: "bean-manager", toolName: "Bean Manager",
          h: "Moisture and density in two seconds.",
          does: "CoffMeter M1 measures moisture to ±0.2% and density to ±1 g/L in under two seconds, on green, parchment, dried cherries or roasted coffee. The figures a buyer asks for first, saved to the lot in CoffeeOS.",
          spec: "Moisture ±0.2% · density ±1 g/L · 2 s",
          shot: "os-app-link-1",
          shotAlt: "Bean Manager in CoffeeOS: green lots listed with the moisture of each",
          callouts: [
            { x: 50, y: 38, t: "Menglian Badu YCT, 9.9% moisture" },
            { x: 50, y: 74, t: "Every lot, with its moisture" }
          ],
          why: {
            green: "Moisture and density, the figures a buyer asks for first, at a price that makes sense for a mill.",
            q: "A second device for cross-checks, affordable enough to keep at origin.",
            roast: "Density and moisture help you set the charge temperature and judge how long the drying phase will take.",
            bar: "The device behind the moisture figure on a green spec sheet.",
            hobby: "Know the moisture and density of a bag, so you can see why the same recipe drifted.",
            teach: "Affordable enough that a class can have several going at once.",
            comp: "Moisture and density on the bag you are about to roast, which help you set the charge temperature."
          }
        },
        {
          slug: "coffmeter-w1",
          name: "CoffMeter W1",
          img: "os-farm-w1-wood", pos: "50% 50%",
          alt: "A hand setting CoffMeter W1 down on a table, green beans in the dish",
          tool: "bean-manager", toolName: "Bean Manager",
          h: "Water activity, before it ships.",
          does: "CoffMeter W1 measures water activity from 0.05 to 0.99 aw, ±0.02, in about two minutes. Water activity predicts how a lot will store. Save it to the lot in CoffeeOS with the moisture.",
          spec: "0.05 to 0.99 aw · ±0.02 · ~2 min",
          shot: "os-app-bean-3",
          shotAlt: "Bean qualities in CoffeeOS with the import menu open: Device Auto-Fill or Photo Recognition",
          callouts: [
            { x: 50, y: 31, t: "Imported from a device, or read from a photo" },
            { x: 50, y: 45, t: "Water activity, from W1" }
          ],
          why: {
            green: "A dedicated water activity meter, for when you already measure moisture another way.",
            q: "A second water activity reading at origin, without buying a second Omix Plus.",
            roast: "Know how a lot will store before you commit to it.",
            bar: "The figure behind the shelf-life claim on a bag.",
            hobby: "Know whether a bag was stored well before you roast or brew it.",
            teach: "Water activity and moisture, side by side, so the difference stops being abstract.",
            comp: "Water activity, so a lot bought months before the competition is still the lot you tasted."
          }
        },
        {
          slug: "r2-pp",
          name: "R2 PP",
          img: "os-farm-pp-brix", pos: "50% 50%",
          alt: "R2 PP reading 14.5 Brix beside a dish of coffee cherries",
          tool: "", toolName: "", mark: "Before the first green reading",
          h: "Pick cherries by Brix.",
          does: "R2 PP measures the sugar in cherry juice as degrees Brix, so picking and sorting ripeness is a reading, not a color judgment. One unit covers one range: 0 to 35, 0 to 55 or 0 to 95 °Brix.",
          spec: "0 to 35, 0 to 55 or 0 to 95 °Brix",
          shot: "",
          why: {
            green: "Brix at the tree and at the wet mill, so ripeness is a figure you can set a standard on.",
            q: "A ripeness figure from the cherry, before any of the green measurements exist.",
            roast: "Ask your producer for the Brix of the cherries your lot came from.",
            bar: "The first measurement in a coffee's life: ripeness at the tree.",
            hobby: "Where a lot's story starts: the sugar in the cherry.",
            teach: "The first measurement in the chain, and affordable enough for a field trip.",
            comp: "Ask for the Brix of the cherries, and the story of your coffee starts at the tree."
          }
        }
      ],

      share: {
        h: "Share each lot with your buyers.",
        img: "os-app-share-qr",
        alt: "Sharing a bean from CoffeeOS as a QR code",
        callouts: [
          { x: 19, y: 38, t: "Scan with CoffeeOS", point: true },
          { x: 59, y: 47, t: "The bean's details are included", point: true },
          { x: 88, y: 68, t: "Opens in the recipient's CoffeeOS" }
        ],
        b: {
          green: "Share the lot's record with your buyers. They open it in their own CoffeeOS with every reading intact.",
          q: "Send the sample's page, with every figure and its method, to whoever asked for the report.",
          roast: "Ask your importer for the lot's page, and you have the green readings in your CoffeeOS before the coffee arrives.",
          bar: "Ask your roaster for the lot's page, and the moisture and density behind the bag are on your phone.",
          hobby: "Scan the code on a bag from a roaster who uses CoffeeOS and see where the coffee came from and what it measured.",
          teach: "Share once and the whole class has the lot, with every reading attached.",
          comp: "Hand a coach the whole coffee: origin, process and every green reading, before the first practice roast.",
          default: "Share the lot and its readings with anyone on CoffeeOS."
        }
      },

      os: {
        title: "Every measurement, saved to the coffee it came from.",
        note: "Results from your devices are imported into each coffee's record in CoffeeOS, with its origin, process and harvest. Free on iOS and Android.",
        shots: [
          { img: "os-app-beans", cap: "Beans, filtered by process. Color and roast date on each." }
        ]
      },

      learn: [
        { label: "Try Omix", url: "/pages/experience-omix-plus", note: "See every screen before you buy", icon: "bean-manager" },
        { label: "Omix Plus complete guide", url: "/blogs/difluid-resources/omix-plus-complete-guide", note: "Every measurement, explained", icon: "bean-manager" },
        { label: "The five green measurements", url: "/blogs/difluid-resources/5-green-bean-metrics-that-quietly-decide-your-cup-score", note: "Moisture, water activity, density, screen size", icon: "notes-manager" }
      ],

      next: [
        { slug: "roast", label: "Roast", img: "os-roast-family" },
        { slug: "brew", label: "Brew", img: "os-brew-moment-hand" }
      ]
    },

    /* ═══════════════ ROAST ═══════════════
       System-first layout, painted by assets/os-room.js. What is
       particular to this page: four instruments measure roast color
       and all four fill the same Tool, so the flow converges on the
       Roast Color Analyzer, and the sharing block is about the QR
       code on the bag. */
    roast: {
      slug: "roast",
      eyebrow: "Roast",
      title: "Roast",
      hero: "os-roast-hero",
      heroAlt: "OmniFlux on a roaster, Omix Plus alongside",

      head: {
        roaster: {
          h: "Match production to the sample.",
          n: "Roast by Color with a live color curve, measure whole beans and grounds after the drop, and save every batch to the bean it came from."
        },
        home: {
          h: "Know every batch's color.",
          n: "The measurement a production roastery takes, on a kitchen counter, with each batch saved in CoffeeOS."
        },
        producer: {
          h: "Show buyers how it roasts.",
          n: "Send a sample roast with its color measured, not only a tasting note."
        },
        exporter: {
          h: "A roast report with the sample.",
          n: "Roast color and the roast delta, saved to the lot and shared with the offer."
        },
        importer: {
          h: "Judge the coffee, not the roaster.",
          n: "The same color measurement on every offer you cup, saved to the lot it belongs to."
        },
        q: {
          h: "Color as a measurement.",
          n: "Whole beans and grounds on one scale, with the color distribution of each, not only an average."
        },
        cafe: {
          h: "Know what changed in the bag.",
          n: "When the roast color of a bag changes, the shot changes. Measure it, and ask your roaster for their curve."
        },
        hobby: {
          h: "Why bags taste different.",
          n: "Roast color and its distribution, measured."
        },
        educator: {
          h: "Watch development happen.",
          n: "Students roast, measure color before and after, and hand in a session a class can compare."
        }
      },

      intro: "",

      system: {
        eyebrow: "The system for you",
        cta: "See what is in it",
        roast: {
          h: "roast-control-system-ultimate-omix-plus-omniflux-airwave", name: "Roast Control System Ultimate",
          lead: "Omix Plus measures the green before the charge, so you know the density and moisture you are about to roast. OmniFlux mounts at the roaster window and draws the color curve as the roast runs, so you Roast by Color and match a production batch to the sample. AirWave takes the smoke out of the room and, paired with OmniFlux, adjusts its airflow to each stage of the roast."
        },
        home: {
          h: "roastery-starter-kit-coffmeter-a1-m1", name: "Roastery Starter Kit",
          lead: "CoffMeter M1 measures the moisture and density of the green, which help you set a charge temperature. CoffMeter A1 measures the roast color after the drop. Save both to the batch in CoffeeOS and compare each roast with the last."
        },
        q: {
          h: "pro-roaster-barista-bundle-omix-r2-microbalance-ti", name: "Roast Validation System Ultimate",
          lead: "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample, with the result of every test, not only the average. Brew the roast on a fixed recipe with Microbalance Ti, measure the extraction with R2 Extract, and link the cup and the roast to the same coffee in CoffeeOS."
        },
        green: {
          h: "roast-validation-system-coffmeter-a1-m1-w1-microbalance-ti-r2-extract-particle-analysis-coaster", name: "Roast Validation System",
          lead: "CoffMeter M1, W1 and A1 measure the green and the sample roast. You brew it on Microbalance Ti and measure it with R2 Extract, the way your buyer will. Save everything to the lot, and share the roast and brew results with the sample."
        },
        bar: {
          h: "roastery-starter-kit-coffmeter-a1-m1", name: "Roastery Starter Kit",
          lead: "CoffMeter A1 measures the roast color of the bag in three seconds and CoffMeter M1 the moisture and density. When a shot drifts, you can see whether the beans changed before you touch the grinder."
        },
        teach: {
          h: "roast-control-system-core-omix-plus-omniflux", name: "Roast Control System Core",
          lead: "Omix Plus measures the green before the roast and the color after it. OmniFlux draws the color curve live, so a class watches development happen. Students save every session and share it in CoffeeOS."
        },
        comp: {
          h: "roast-control-system-core-omix-plus-omniflux", name: "Roast Control System Core",
          lead: "Omix Plus measures what went in and OmniFlux draws the color curve of every practice roast, so the roast you compete on is the one your records show worked."
        }
      },

      /* Four instruments, one Tool. The flow converges. */
      flow: {
        h: "Every color meter works with the same Tool.",
        n: {
          roast: "Measure with whichever device suits the moment, live in the drum or on the sample tray. Every reading is imported into the same Roast Color Analyzer in CoffeeOS and saved to the batch.",
          green: "Compare a sample roast measured at origin with the same roast measured on arrival, in the same Tool.",
          bar: "Your roaster's reading and yours are in the same Tool, so you can see exactly how much a bag changed.",
          teach: "Students can measure with any of the four and the class compares results in one place.",
          comp: "Every practice roast measured the same way, in the same Tool, saved to the coffee you compete on.",
          default: "Whichever device you use, the reading is imported into the same Tool and saved to the same bean."
        },
        cols: ["Measure", "In CoffeeOS", "On the bean"],
        links: [
          { gear: "omniflux",     name: "OmniFlux",     measure: "Roast by Color, live in the roast", tool: "roast-color", toolName: "Roast Color Analyzer", toolLine: "Whole bean, grounds, roast delta" },
          { gear: "omni",         name: "Omni",         measure: "Color and grind size, from 5 g",    tool: "roast-color", toolName: "Roast Color Analyzer", toolLine: "Whole bean, grounds, roast delta" },
          { gear: "omix-plus",    name: "Omix Plus",    measure: "Color, plus the green readings",    tool: "roast-color", toolName: "Roast Color Analyzer", toolLine: "Whole bean, grounds, roast delta" },
          { gear: "coffmeter-a1", name: "CoffMeter A1", measure: "A single Agtron value in three seconds",       tool: "roast-color", toolName: "Roast Color Analyzer", toolLine: "Whole bean, grounds, roast delta" }
        ],
        record: {
          img: "os-app-roastsession",
          alt: "A Roast Color Analyzer session in CoffeeOS: whole bean 91.4 Agtron, grounds 105.8, roast delta -14.4",
          callouts: [
            { x: 50, y: 15, t: "Whole beans 91.4 Agtron" },
            { x: 50, y: 55, t: "Grounds 105.8 Agtron" },
            { x: 50, y: 76, t: "Roast delta -14.4: the difference between the outside and the inside" },
            { x: 50, y: 87, t: "Saved to the batch" }
          ]
        }
      },

      tools: [
        {
          slug: "omniflux",
          name: "OmniFlux",
          img: "os-roast-flux", pos: "50% 50%",
          alt: "OmniFlux mounted at a roaster window, the color curve on its screen",
          tool: "roast-color", toolName: "Roast Color Analyzer",
          h: "Roast by Color.",
          does: "OmniFlux mounts outside the roaster window, with no modification to the roaster, and tracks the color of the beans through the roast, ±1 Agtron from 20 to 60 cm. Set a target color and it alerts you when the roast gets there. Yellowing, first crack and second crack are marked for you. Off the stand it measures whole beans and grounds to ±0.1 Agtron.",
          spec: "Live ±1 Agtron · static ±0.1 · marks cracks",
          shot: "",
          why: {
            roast: "Watch the color curve during the roast instead of finding out after the drop, and match a production batch to the sample roast.",
            green: "A live color curve to send beside the sample roast you shipped.",
            q: "Color on a time axis rather than one end point.",
            bar: "Ask your roaster for the color curve of the roast on your shelf.",
            hobby: "See the color move as the roast runs, and drop when it reaches your target.",
            teach: "Development stops being a story and becomes something the room can watch.",
            comp: "Watch the color curve and adjust your drop time while there is still time, instead of finding out after the drop."
          }
        },
        {
          slug: "omni",
          name: "Omni",
          img: "os-roast-omni", pos: "35% 30%",
          alt: "Omni measuring roasted beans, the result on its screen",
          tool: "roast-color", toolName: "Roast Color Analyzer",
          h: "Whole bean, grounds, roast delta.",
          does: "Omni measures roast color from 1 to 150 Agtron to ±0.1, on whole beans and on grounds, from about five grams. The Roast Color Analyzer in CoffeeOS puts the two side by side and shows the roast delta between them, which tells you whether the inside of the bean developed as far as the outside. Omni also measures grind size.",
          spec: "1 to 150 Agtron · ±0.1 · ~5 g",
          shot: "os-app-roastdelta",
          shotAlt: "The Roast Color Analyzer in CoffeeOS: whole bean 55.5 Agtron, grounds 73.5, roast delta -18",
          callouts: [
            { x: 50, y: 15, t: "Whole bean distribution, 55.5 Agtron" },
            { x: 50, y: 55, t: "Grounds 73.5 Agtron" },
            { x: 50, y: 76, t: "Roast delta -18" }
          ],
          why: {
            roast: "Measure whole beans and grounds on one device after every batch, and compare the roast delta with the sample roast.",
            green: "The finished side of the coffee you sold, measured the same way at both ends.",
            q: "Color and distribution, each reported with a standard deviation.",
            bar: "The grind check and the color check in one device.",
            hobby: "Roast color and grind size from one device, so you can see which one changed the cup.",
            teach: "Roast color and grind size from one device.",
            comp: "Grounds color as a distribution, from five grams, small enough to check a roast without spending the bag you are saving."
          }
        },
        {
          slug: "omix-plus",
          name: "Omix Plus",
          img: "os-roast-omix", pos: "50% 45%",
          alt: "Omix Plus over a dish of green coffee beside the roaster",
          tool: "bean-manager", toolName: "Bean Manager",
          h: "Measure the green before you charge it.",
          does: "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample. Before the roast, density and moisture help you set the charge temperature. After the drop, it gives the final color with the result of every test, not just an average. Every result is imported into the bean's record in CoffeeOS.",
          spec: "Moisture ±0.1% · density ±0.5 g/L · color ±0.1",
          shot: "os-app-bean-4",
          shotAlt: "Bean qualities in CoffeeOS imported from Omix Plus: moisture 9.9%, water activity 0.638, bulk density 825.1 g/L",
          callouts: [
            { x: 50, y: 38, t: "Moisture 9.9%" },
            { x: 50, y: 44, t: "Water activity 0.638" },
            { x: 50, y: 50, t: "Bulk density 825.1 g/L" },
            { x: 50, y: 62, t: "Screen size 19 to 21" }
          ],
          why: {
            roast: "Density and moisture help you set the charge temperature, and both come off the same sample you are about to roast.",
            green: "The same device at the patio and at the roaster, so the readings are comparable.",
            q: "One device for the green measurements and the roast color distribution.",
            bar: "The device behind the moisture and density your roaster quotes to you.",
            hobby: "Know the moisture and density of a bag before you roast it, so a batch that behaved differently can be explained.",
            teach: "Green grading and roast analysis without buying twice.",
            comp: "What went in and what came out, off one sample, so you can explain a batch that behaved differently."
          }
        },
        {
          slug: "airwave",
          name: "AirWave",
          img: "os-roast-family", pos: "50% 50%",
          alt: "AirWave beside Omix Plus and OmniFlux",
          tool: "", toolName: "", mark: "Pairs with OmniFlux",
          h: "Roast indoors.",
          does: "AirWave breaks roasting smoke down into carbon dioxide and water vapor with a low-temperature catalyst, so a roaster up to 1 kg (800 g on 120 V) can run indoors without a duct to the street. Paired with OmniFlux it adjusts its own airflow to each stage of the roast, so you set it once and roast.",
          spec: "To 1 kg · 99.9% purification",
          shot: "",
          why: {
            roast: "Roast indoors without running a duct outside, and let the fan follow the roast once OmniFlux is paired.",
            green: "Sample roast in the sample room, without an extraction hood.",
            q: "Sample roasting where the samples are.",
            bar: "Roast in the café without smoking out the street.",
            hobby: "Roast in an apartment, at any hour, without the smoke.",
            teach: "A roasting class in a room that was never built for one.",
            comp: "Roast indoors at whatever hour you practice, without the smoke."
          }
        },
        {
          slug: "coffmeter-a1",
          name: "CoffMeter A1",
          img: "os-roast-a1-screen", pos: "50% 40%",
          alt: "CoffMeter A1 reading 92.5 Agtron on roasted beans",
          tool: "roast-color", toolName: "Roast Color Analyzer",
          h: "Roast color in three seconds.",
          does: "CoffMeter A1 measures the roast color of whole beans or grounds in three seconds, 0 to 150 Agtron, ±0.5. It gives a single value rather than a distribution. Save it to the batch in CoffeeOS with your other readings.",
          spec: "0 to 150 Agtron · ±0.5 · 3 s",
          shot: "os-app-link-2",
          shotAlt: "Roasted beans in CoffeeOS, each listed with its Agtron value",
          callouts: [
            { x: 50, y: 22, t: "Normal Flow 220: 70.4 Agtron" },
            { x: 50, y: 60, t: "Every batch, with its Agtron" }
          ],
          why: {
            roast: "A color check for a shop that needs one value, not a distribution.",
            green: "A cheap second opinion on a sample roast.",
            q: "A single Agtron value, for a fast check between samples.",
            bar: "Enough to tell a light roast from a medium one, with a number.",
            hobby: "The most affordable way to know the roast color of a bag.",
            teach: "Affordable enough for one per workstation.",
            comp: "Fast enough that you will actually check between practice roasts."
          }
        }
      ],

      share: {
        h: "Put the roast on the bag.",
        img: "os-app-share-roast",
        alt: "Sharing a Roast Analysis Session from CoffeeOS as a card with a QR code",
        callouts: [
          { x: 19, y: 38, t: "Scan with CoffeeOS", point: true },
          { x: 37, y: 47, t: "The whole session, both tests", point: true },
          { x: 88, y: 68, t: "Opens in the recipient's CoffeeOS" },
          { x: 26, y: 93, t: "Print the card for your bags", point: true }
        ],
        card: "os-app-share-roast-card",
        cardAlt: "The saved card: Roast Analysis Session, Session 13, two tests, with its QR code",
        cardLine: "A card you can print for the bag.",
        b: {
          roast: "Share the bean as a QR code and print it on the bag. A wholesale buyer scans it and sees the roast color, the green readings and your recipe. A customer scans it and brews it the way you meant.",
          green: "Share the sample roast with a buyer, with its color and the lot it came from.",
          bar: "Scan the code on a bag from a roaster who uses CoffeeOS and you see their roast and recipe on your phone.",
          hobby: "Scan the code on a bag from a roaster who uses CoffeeOS and see how the coffee was roasted and how they brew it.",
          teach: "Share once and the whole class has the session, with the roast color and the green readings attached.",
          comp: "Hand a coach the whole roast: the green readings, the color curve and the cup.",
          default: "Share the roast, the green readings and the recipe with anyone on CoffeeOS."
        }
      },

      os: {
        title: "Whole bean, grounds, and the roast delta between them.",
        note: "Save every Roast Color Analyzer test to the bean it was measured on. Free on iOS and Android.",
        shots: [
          { img: "os-app-roastdelta", cap: "Whole bean 55.5 Agtron, grounds 73.5, roast delta −18." }
        ]
      },

      learn: [
        { label: "Compare color meters", url: "/pages/color-meters", note: "A1, Omni, OmniFlux, Omix Plus", icon: "roast-color" },
        { label: "Omni complete guide", url: "/blogs/difluid-resources/omni-complete-guide", note: "Every screen, in order", icon: "particle-analyzer" },
        { label: "Understanding color measurement", url: "/blogs/difluid-resources/understanding-color-measurement-in-coffee", note: "Why two meters disagree", icon: "roast-color" }
      ],

      next: [
        { slug: "farm", label: "Farm & green", img: "os-farm-pp-cherries" },
        { slug: "brew", label: "Brew", img: "os-brew-moment-hand" }
      ]
    },

    /* ═══════════════ BREW ═══════════════
       The first stage page on the system-first layout. The order a
       visitor reads: the stage, one line for their role, the system
       for that role, how its readings reach the coffee, then each
       device with the CoffeeOS screen it fills, then sharing, then
       Learn. Painted by assets/os-room.js. */
    brew: {
      slug: "brew",
      eyebrow: "Brew",
      title: "Brew",
      hero: "os-brew-r2-hand",
      heroAlt: "A hand dropping coffee onto R2 Extract on a tray, beside the cup and the dripper",

      head: {
        cafe: {
          h: "The same cup, every shift.",
          n: "Record the recipe once. Anyone on the bar can follow it, and you can save every brew to the bean it was made with."
        },
        hobby: {
          h: "Why today's cup was better.",
          n: "Measure the grind, the pour and the extraction, then taste the cup at the right temperature. Change one thing at a time and see what it did."
        },
        roaster: {
          h: "Taste what the roast did.",
          n: "Brew every batch on one fixed recipe and measure the extraction. If the cup changed, the roast changed, and you can link both to the same bean in CoffeeOS."
        },
        home: {
          h: "Brew it to check development.",
          n: "A fixed recipe and a TDS reading show whether the batch developed. Save every result to the batch it came from."
        },
        q: {
          h: "Extraction yield you can defend.",
          n: "TDS by refractometer, dose and yield by scale, with the extraction calculated in CoffeeOS and the recipe recorded with it."
        },
        producer: {
          h: "Know how your coffee tastes.",
          n: "Brew your own coffee, measure the extraction, and describe the cup to buyers with confidence."
        },
        exporter: {
          h: "Send a brew report too.",
          n: "A recorded brew with its TDS backs up your tasting notes. Save it to the lot and share it with the offer."
        },
        importer: {
          h: "Compare offers on one recipe.",
          n: "Same dose, same ratio, same grind, so the difference you taste comes from the coffee. Save every cup to the lot it came from."
        },
        educator: {
          h: "Teach extraction as it happens.",
          n: "Students pour on the scale, read the TDS, and see the brew plotted on the chart, then share every session with you in CoffeeOS."
        }
      },

      intro: "",

      /* The system for this reader. Keyed by role, then family. The
         handle names a product the section resolves from Shopify. */
      system: {
        eyebrow: "The system for you",
        cta: "See what is in it",
        bar: {
          h: "sensory-brew-control-system", name: "Sensory Brew Control System",
          lead: "Microbalance Ti records every brew as the pour happens and R2 Extract measures the TDS, so each recipe on the bar has a measured result behind it. Moment shows customers what to taste as the cup cools, and the Particle Analysis Coaster lets you check the grind when the grinder drifts."
        },
        hobby: {
          h: "sensory-brew-control-system", name: "Sensory Brew Control System",
          lead: "Microbalance Ti records the pour and R2 Extract measures the extraction, so you can see what one change did to the cup. Use the Particle Analysis Coaster to check the grind, and Moment to follow a roaster's tasting notes as the coffee cools."
        },
        comp: {
          h: "sensory-brew-control-system", name: "Sensory Brew Control System",
          lead: "Microbalance Ti records every pour of every practice run and R2 Extract measures the extraction, so you can see what made one run better than another and repeat it on stage. Moment tracks the temperature of the cup as it cools, so you know when it should be tasted."
        },
        roast: {
          h: "ultimate-coffee-brew-control-system-omni-microbalance-ti-r2-extract", name: "Brew Control System Ultimate (Ti)",
          lead: "Omni measures the grind and the roast color, Microbalance Ti records the brew, and R2 Extract measures the extraction. Brew every batch the same way and the extraction shows what the roast did."
        },
        green: {
          h: "brew-control-system-ti-microbalance-ti-r2-extract", name: "Brew Control System Ti",
          lead: "Microbalance Ti records the brew and R2 Extract measures the extraction, so a sample is brewed the same way at both ends of a shipment. Save the result to the lot."
        },
        q: {
          h: "brew-control-system-ti-microbalance-ti-r2-extract", name: "Brew Control System Ti",
          lead: "Dose and yield from the scale, TDS from the refractometer, and the extraction calculated in CoffeeOS with the recipe recorded beside it."
        },
        teach: {
          h: "ultimate-coffee-brew-control-system-omni-microbalance-ti-r2-extract", name: "Brew Control System Ultimate (Ti)",
          lead: "Omni for grind size, Microbalance Ti for the pour, R2 Extract for TDS: enough to teach grind, extraction and strength from one set of devices, and students can save every session."
        }
      },

      /* How a reading moves: device, the Tool it fills, the coffee it
         is saved to. Drawn with the real device renders, the real Tool
         tiles and a real Bean Insights screen. */
      flow: {
        h: "Every reading is saved to the same coffee.",
        n: {
          bar:   "Record a brew on the bar and save it to the bean with the recipe, the TDS and the roast color of the bag.",
          hobby: "Save every brew to the coffee you made it with, so next week you can see what you did and do it again.",
          roast: "Link the green measurements, the roast color and the brew to the same bean, and you can trace a change in the cup back to the roast.",
          green: "Share the brew with your buyer along with the lot it came from.",
          teach: "Students save each session to the coffee it was run on, with the recipe recorded.",
          comp:  "Twenty practice runs on one coffee, each with its grind, pour and extraction, in one place.",
          default: "Each device works with a Tool in CoffeeOS, and you can save the results to the bean."
        },
        cols: ["Measure", "In CoffeeOS", "On the bean"],
        links: [
          { gear: "microbalance-ti", name: "Microbalance Ti", measure: "Weight and flow", tool: "brew-recorder", toolName: "Brew Recorder", toolLine: "The pour, saved as a recipe" },
          { gear: "r2-extract",      name: "R2 Extract",      measure: "TDS",             tool: "brew-controller", toolName: "Brew Controller", toolLine: "Extraction, plotted on the chart" },
          { gear: "omni",            name: "Omni",            measure: "Grind size and roast color", tool: "particle-analyzer", toolName: "Particle Analyzer", toolLine: "D10, D50, D90 and how even the grind is" },
          { gear: "moment",          name: "Moment",          measure: "Cup temperature", tool: "flavor-thermometer", toolName: "Flavor Thermometer", toolLine: "What to taste at each temperature" }
        ],
        record: {
          img: "os-app-insights",
          alt: "A bean's page in CoffeeOS: its recipe, four brew records, two roast color tests and the green lot it came from",
          callouts: [
            { x: 50, y: 44, t: "The recipe for this coffee" },
            { x: 50, y: 67, t: "Four brews recorded on it" },
            { x: 50, y: 74, t: "Two roast color tests" },
            { x: 50, y: 90, t: "The green lot it came from" }
          ]
        }
      },

      /* One block per device. `shot` is the CoffeeOS screen that
         device fills, with the figures a visitor should see called
         out beside it. `why` changes with the role. */
      tools: [
        {
          slug: "microbalance-ti",
          name: "Microbalance Ti",
          img: "os-brew-mb-pour", pos: "50% 40%",
          alt: "A pour-over being made on Microbalance Ti, R2 Extract beside it",
          tool: "brew-recorder", toolName: "Brew Recorder",
          h: "Every pour, recorded live.",
          does: "Microbalance Ti weighs to 0.1 g with a 3,000 g capacity and sends weight and flow rate to CoffeeOS over Bluetooth. The Brew Recorder draws the pour as a curve, and you can save a brew as a recipe anyone can follow.",
          spec: "0.1 g · 3,000 g · flow rate",
          shot: "os-app-brewresult",
          shotAlt: "A recorded brew in CoffeeOS: dose 15 g, water 229 g, ratio 1:15.2, with each pour drawn as a curve",
          callouts: [
            { x: 18, y: 16, t: "Dose 15 g, water 229 g, ratio 1:15.2" },
            { x: 42, y: 47, t: "Each pour, drawn as it happened", point: true }
          ],
          why: {
            bar:   "Dose and yield, and a flow trace that shows where a pour went sideways.",
            hobby: "Dose and yield to a tenth of a gram, and a flow trace that shows where your pour sped up.",
            teach: "Weight and time on one screen, so the class watches one thing.",
            roast: "The other half of extraction yield.",
            green: "The scale under a sample brew.",
            q:     "Dose and beverage mass, recorded, so the yield calculation has inputs."
          }
        },
        {
          slug: "r2-extract",
          name: "R2 Extract",
          img: "os-brew-r2-drop", pos: "50% 30%",
          alt: "A drop of coffee going onto the R2 Extract sensor",
          tool: "brew-controller", toolName: "Brew Controller",
          h: "Measure the extraction.",
          does: "R2 Extract measures TDS from one drop in about four seconds, 0 to 30%, ±0.02%. Import it into the Brew Controller and the brew is plotted on the chart with its extraction calculated, so you can see what a change to grind, dose or water did.",
          spec: "TDS 0 to 30% · ±0.02% · ~4 s",
          shot: "os-app-brewctl",
          shotAlt: "The Brew Controller in CoffeeOS: a cup plotted on the brew control chart, TDS 1.18%, extraction 16.2%",
          callouts: [
            { x: 29, y: 34, t: "This cup's position on the chart", point: true },
            { x: 63, y: 68, t: "TDS 1.18%" },
            { x: 63, y: 75.5, t: "Extraction 16.2%" }
          ],
          why: {
            bar:   "Extraction on the bar, between orders, in about four seconds.",
            hobby: "Whether the change you made actually changed the cup, measured before it goes cold.",
            q:     "A figure with a stated precision, which is what makes it quotable.",
            roast: "Whether the roast is soluble, not merely dark.",
            teach: "Extraction stops being a formula on a slide.",
            green: "Taste your own lot the way the person buying it will."
          }
        },
        {
          slug: "moment",
          name: "Moment",
          img: "os-brew-moment-app", pos: "50% 25%",
          alt: "Moment over a glass of coffee, with its profile open in CoffeeOS on a phone",
          tool: "flavor-thermometer", toolName: "Flavor Thermometer",
          h: "Taste by Temperature.",
          does: "Moment stands over the cup and measures the coffee as it cools, showing the flavors to expect at each temperature. Profiles come from a roaster, a barista or you, so you taste the same coffee against the same notes every time.",
          spec: "Up to five stages · 200 profiles",
          shot: "os-app-flavor",
          shotAlt: "A Moment profile in CoffeeOS: three temperature stages, each with its flavor notes",
          callouts: [
            { x: 50, y: 16, t: "Hot, 95 to 75 °C: mango and berries" },
            { x: 50, y: 69, t: "Cooling, 40 to 20 °C: mango and light body" }
          ],
          why: {
            bar:   "Customers know what to taste in each cup, and they can rate what they tasted.",
            hobby: "Follow a roaster's notes through the temperature range, or write your own and see if you taste it the same way next time.",
            teach: "A class tastes the same coffee against the same notes, and each student can rate whether they agreed.",
            roast: "Tell the people drinking your coffee what to taste, and see which stages they liked.",
            green: "Tell buyers how your coffee should taste.",
            q:     "A tasting run at the same temperature stages every time.",
            comp:  "Know when the cup should be tasted, based on how it was brewed."
          }
        },
        {
          slug: "omni",
          name: "Omni",
          alsoSlug: "coaster", alsoName: "Particle Analysis Coaster",
          img: "os-brew-omni",
          alt: "Grounds being tipped into the Omni sample dish",
          tool: "particle-analyzer", toolName: "Particle Analyzer",
          h: "The grind, in microns.",
          does: "Omni measures the particle size distribution of a few grams of grounds, and the roast color of the beans you are brewing. The Particle Analysis Coaster measures the grind with your phone's camera. Either way the Particle Analyzer in CoffeeOS shows D10, D50, D90 and how even the grind is, so a setting means a measured grind size and a change in the cup can be traced to the grinder or the roast.",
          spec: "D10 / D50 / D90 · roast color ±0.1 Agtron",
          shot: "os-app-d50",
          shotAlt: "The Particle Analyzer in CoffeeOS: a grind distribution with D50 618 µm",
          callouts: [
            { x: 56, y: 30, t: "The shape of the grind", point: true },
            { x: 50, y: 59, t: "D50 618 µm: half the particles are smaller" },
            { x: 50, y: 68, t: "SD 321.8: how even the grind is" }
          ],
          why: {
            bar:   "See exactly how far the grinder drifted.",
            hobby: "Convert a setting between two grinders using their measured distributions.",
            green: "The grind under a sample brew, held still, so the cup is the lot and not the setting.",
            q:     "D10, D50, D90 and the standard deviation, reported.",
            roast: "Grind held still, so the variable you are testing is the roast.",
            teach: "Fines and boulders, on a curve, in front of the room."
          }
        }
      ],

      share: {
        h: "Share recipes with anyone on CoffeeOS.",
        img: "os-app-share-qr",
        alt: "Sharing a brew recipe from CoffeeOS as a QR code",
        callouts: [
          { x: 19, y: 38, t: "Scan with CoffeeOS", point: true },
          { x: 88, y: 68, t: "Opens in the recipient's CoffeeOS" }
        ],
        b: {
          bar:   "Share a recipe as a QR code and the next barista scans it into their CoffeeOS: dose, water, grind size, water temperature and the bean it belongs to.",
          hobby: "Scan the code on a bag from a roaster who uses CoffeeOS and their recipe opens on your phone, ready to brew. Share your own the same way.",
          roast: "Put the QR code on the bag. A customer scans it and gets your recipe for that roast in CoffeeOS, with the bean details attached.",
          green: "Share the brew you tasted with a buyer, with the lot it came from.",
          teach: "Share once and the whole class has the recipe. Students share their results back the same way.",
          comp:  "Hand a coach or teammate the whole routine: the recipe, the readings and the coffee.",
          default: "Share the recipe and the bean it belongs to with anyone on CoffeeOS."
        }
      },

      os: {
        title: "The Brew Recorder records the brew while you pour.",
        note: "The Brew Recorder records the pour from the scale, and the Brew Controller calculates the extraction from the TDS. Free on iOS and Android.",
        shots: [
          { img: "os-app-record", cap: "Flow and weight, recorded live. Dose 15 g, water 229 g, 1:15.2." },
          { img: "os-app-tds", cap: "R2 Extract connected. TDS 1.18%, extraction 16.2%." },
          { img: "os-app-d50", cap: "D50 618 µm, SD 321.8. The grind, on a curve." }
        ]
      },

      learn: [
        { label: "R2 Extract complete guide", url: "/blogs/difluid-resources/r2-extract-complete-guide", note: "TDS, and what to do with it", icon: "brew-controller" },
        { label: "Grinder Manager setup", url: "/blogs/difluid-resources/coffeeos-grinder-manager-setup-guide", note: "Burrs, retention, setting history", icon: "grinder-manager" },
        { label: "Taking control of your brew", url: "/pages/brew-control-system-intro", note: "Dose, yield, TDS and what each one tells you about the cup", icon: "brew-recorder" }
      ],

      next: [
        { slug: "farm", label: "Farm & green", img: "os-farm-pp-cherries" },
        { slug: "roast", label: "Roast", img: "os-roast-family" }
      ]
    }
  };


  /* ── CoffeeOS ───────────────────────────────────── */

  var COFFEEOS = {

    /* ── The walk over Particle Analyzer ──────────────────
       Three parts of the Tool's screen, lit one at a time as the page
       scrolls. Painted by os-walk.js. Concept only: what the reader is
       looking at and why it matters, not which button does what. */
    walk: {
      particle: {
        default: [
          {
            part: "chart",
            k: "The chart",
            h: "The shape of the grind",
            b: "Particle size along the bottom, and how much of the sample falls at each size, so you see the whole grind instead of one number."
          },
          {
            part: "dvalues",
            k: "D10, D50, D90",
            h: "Three points on the curve",
            b: "D10: 10% of the particles are smaller. D50: half are smaller. D90: 90% are smaller. Together they describe the shape of the grind."
          },
          {
            part: "spread",
            k: "Mode, SD, Mean",
            h: "How even the grind is",
            b: "Mode is the most common size, Mean the average, and SD how spread out the sizes are. Two grinds with the same D50 can brew differently, and SD shows why."
          }
        ]
      }
    },

    /* The opening headline and line, by role. */
    head: {
      producer: {
        h: "Every lot you produce, measured and kept in one place.",
        n: "Moisture, water activity and density for every lot, imported from your devices instead of typed into a spreadsheet, backed up, searchable and open to your whole team."
      },
      exporter: {
        h: "The same data at origin and on arrival.",
        n: "Measure each lot before it ships and share the record with your buyer, who can compare it with their own measurements when the coffee arrives."
      },
      importer: {
        h: "Check what arrived against what you were told.",
        n: "Measure moisture, water activity and density on arrival, and keep every lot you buy in one searchable place, with its data from green to cup."
      },
      q: {
        h: "Measured attributes, with the data behind them.",
        n: "Color, moisture, density and size recorded for each coffee, so any figure you report can be traced back to its measurement."
      },
      roaster: {
        h: "Every roast, measured and easy to find.",
        n: "Record green data before the roast and color after it, link both to the coffee, and compare any batch with the ones before it."
      },
      cafe: {
        h: "Your best recipes, ready for anyone on the bar.",
        n: "Record a brew you like, save it as a recipe, and share it with your team so anyone can follow it step by step."
      },
      hobby: {
        h: "Find out what your grinder is really doing.",
        n: "Measure your grind size distribution, build a profile for your grinder, and see how each change shows up in the cup."
      },
      home: {
        h: "See what changed from one batch to the next.",
        n: "Roast color, moisture and density for every batch, saved in CoffeeOS so you can compare this week's roast with last week's."
      },
      educator: {
        h: "Every student's work, shared with you.",
        n: "Students measure and record their brews in CoffeeOS and share the records with you, so you can see how each of them worked, not only the result."
      },
      competitor: {
        h: "Every practice run, recorded.",
        n: "Record each run with its grind, pour and extraction, and go on stage with the routine your data says works best."
      }
    },

    /* What CoffeeOS is. The same for every reader, and the first
       chapter after the hero. */
    about: {
      sub: "The coffee data platform",
      h: "All of your coffee data, in one place.",
      b: "CoffeeOS is a free app made of Tools, each built for one coffee task: managing your beans, analyzing roast color and grind size, recording brews, and more. DiFluid devices connect over Bluetooth and send their results straight into the app. Every record is backed up, searchable and easy to share with the people you work with, and new DiFluid devices join the same platform as they arrive.",
      toolsTitle: "Tools in CoffeeOS"
    },

    /* How it works, for someone who has never opened the app. */
    how: {
      h: "How CoffeeOS works",
      steps: [
        { n: "1", t: "Measure", b: "Measure your coffee with a DiFluid device, or with equipment you already own." },
        { n: "2", t: "Bring it into the app", b: "Pair a DiFluid device over Bluetooth and import its results directly, or enter values from other devices yourself." },
        { n: "3", t: "Link it to your coffee", b: "Save results to the coffee they belong to, so its green data, roasts, grind and brews are all in one place." },
        { n: "4", t: "Share it", b: "Send any record to the people you work with. They open it in their own CoffeeOS." }
      ]
    },

    /* Free to use, with or without DiFluid devices. */
    free: {
      h: "Free to use, with or without DiFluid devices",
      b: "Every Tool in CoffeeOS is free. The Brew Ratio Calculator and the Grinder Converter work entirely on their own. Print a Particle Analysis Coaster and you can measure grind size with your phone in the Particle Analyzer and build grinder profiles in the Grinder Manager. If you already own a moisture meter or a refractometer, enter its readings by hand. DiFluid devices pair with CoffeeOS and import their results directly, which saves time and avoids typos.",
      devicesTitle: "Every DiFluid device connects to CoffeeOS"
    },

    /* The Brew Ratio Calculator, playable on the page. */
    tryRatio: {
      h: "Try the Brew Ratio Calculator",
      b: "Choose how many servings you want, how strong, and which brewer, and the calculator works out the dose, water and ratio. Water retention accounts for the water your coffee grounds hold back, so the cup comes out the size you planned.",
      how: [
        "Tap any highlighted value to change it.",
        "The drop at the top right turns water retention on and off."
      ]
    },

    /* The Brew Recorder simulator under the Brew Recorder chapter. */
    tryBrew: {
      h: "Try following a recipe in the Brew Recorder",
      b: "A simulator of a pour-over recipe in CoffeeOS. The scale shows how much water you have poured against the target for each step of the recipe.",
      how: [
        "Press and hold in the pour area to pour.",
        "Move up while holding to pour faster.",
        "Keep the yellow bar level with the clock line to match the recipe's timing."
      ]
    },

    /* The Moment simulator. */
    tryMoment: {
      how: ["Drag the temperature down, or press Watch it cool."],
      more: "Everything about Moment"
    },

    /* What each DiFluid device brings into CoffeeOS. */
    devices: [
      { slug: "omix-plus", use: "Moisture, water activity, density, screen size and color, in Bean Manager." },
      { slug: "coffmeter-m1", use: "Moisture and density, in Bean Manager." },
      { slug: "coffmeter-w1", use: "Water activity, in Bean Manager." },
      { slug: "coffmeter-a1", use: "Roast color, in the Roast Color Analyzer." },
      { slug: "omni", use: "Roast color and grind size distribution, in the Roast Color Analyzer and Particle Analyzer." },
      { slug: "omniflux", use: "Roast color during the roast, in the Roast Color Analyzer." },
      { slug: "airwave", use: "Smoke removal that adjusts its airflow to each stage of the roast when paired with OmniFlux." },
      { slug: "microbalance-ti", use: "Weight and flow rate, live in the Brew Recorder." },
      { slug: "r2-extract", use: "TDS and extraction, in the Brew Controller." },
      { slug: "moment", use: "Taste by Temperature, with profiles from Flavor Thermometer." }
    ],

    /* ── What order the page runs in ─────────────────────────
       Each family gets its own running order. A chapter left out of
       a family's list is not shown to that family: the green and
       roasting families meet the Bean Manager in one chapter, so the
       grind walk is only shown where the grind is the point. */
    order: {
      green:   ["origin", "record", "brew", "insights", "share", "moment"],
      roast:   ["record", "insights", "brew", "share", "moment", "origin"],
      bar:     ["brew", "moment", "field", "insights", "share", "record", "origin"],
      teach:   ["field", "brew", "record", "insights", "share", "moment", "origin"],
      comp:    ["brew", "moment", "field", "insights", "record", "share", "origin"],
      default: ["origin", "record", "brew", "insights", "share", "moment"]
    },

    /* Which photograph opens the page. File names in Content > Files. */
    hero: {
      producer: "os-cos-patio.jpg",
      exporter: "os-cos-green.jpg",
      importer: "os-cos-green.jpg",
      q:        "os-cos-omix-green.jpg",
      roaster:  "os-roast-hero.jpg",
      home:     "os-roast-omix.jpg",
      cafe:     "os-cos-counter.jpg",
      hobby:    "os-brew-hero.jpg",
      educator: "os-cos-ecosystem.jpg",
      competitor: "os-cos-counter.jpg",
      default:  "os-cos-cherries.jpg"
    },

    /* ── The chapters ────────────────────────────────────────
       One headline and one paragraph each, keyed by role, then by
       family (green, roast, bar, teach, comp), then default. */
    acts: {

      origin: {
        tool: null,
        h: {
          producer: "Show buyers the data behind your coffee.",
          exporter: "Give buyers data they can check.",
          importer: "The farm, the process and the data, together.",
          q:        "Know where a sample came from.",
          roast:    "The farm behind the coffee you roast.",
          bar:      "The farm behind the bag on your shelf.",
          teach:    "Teach the whole chain, from farm to cup.",
          comp:     "Know the coffee you compete with.",
          default:  "Show buyers the data behind your coffee."
        },
        b: {
          producer: "Season after season, your measurements build a history: how long each lot took to dry, how it held up in storage, how close it came to your target moisture. That history backs up your process when a buyer asks about it, and it builds trust. When your buyers use CoffeeOS too, they can scan the QR code on your record and see the data for themselves.",
          exporter: "Measure every lot before it ships and keep the history in CoffeeOS: moisture, water activity and density, lot by lot and season by season. Share the record with your buyer, and if they measure on arrival, they can compare their readings with yours in the same app.",
          importer: "Every lot you buy has a farm, a process and a harvest behind it. Keep the crop information and your arrival measurements together in CoffeeOS, and when a producer or exporter shares their records with you, open their data in the same app.",
          q:        "Origin, variety, process and altitude, kept in CoffeeOS with the moisture, water activity and density you measured, so every result you report comes with its context.",
          roast:    "Crop information and green data are part of the record too. When a supplier shares their records in CoffeeOS, you can open the producer's data on the coffee you are about to roast.",
          bar:      "Before it was a recipe, it was a lot with a farm, a process and a harvest. When your roaster shares their records in CoffeeOS, you can see where the coffee came from and how it was measured along the way.",
          teach:    "Green grading at one end, extraction at the other, and the same app for both, so a course can follow one coffee from the farm to the cup.",
          comp:     "Origin, process, altitude, moisture and density, kept with the coffee in CoffeeOS and ready for the questions judges ask.",
          default:  "Season after season, your measurements build a history that backs up your process and builds trust with buyers."
        }
      },

      record: {
        tool: { icon: "bean-manager", name: "Bean Manager", sub: "Your coffee inventory" },
        shot: {
          green:   { img: "os-cos-omix-farm", alt: "Omix Plus sample cup filled from a bed of drying coffee" },
          roast:   { img: "os-roast-flux",    alt: "Green coffee going into Omix Plus, water activity and density on the screen" },
          bar:     { img: "os-roast-omix",    alt: "Omix Plus over a dish of green coffee at the roastery" },
          teach:   { img: "os-roast-omix",    alt: "Omix Plus over a dish of green coffee" },
          default: { img: "os-cos-omix-farm", alt: "Omix Plus sample cup filled from a bed of drying coffee" }
        },
        h: {
          producer: "One record for each lot, from crop information to quality data.",
          exporter: "A record for every lot you ship.",
          importer: "A record for every lot you buy.",
          q:        "Green data you can trace.",
          roaster:  "Know the green before you charge it.",
          home:     "Know the green before you charge it.",
          bar:      "Know the coffee you are dialing in.",
          teach:    "Coffee records students build themselves.",
          comp:     "One record for each coffee you compete with.",
          default:  "One record for each lot, from crop information to quality data."
        },
        b: {
          producer: "Create a record for each lot with its origin, variety, process, altitude and harvest date. Moisture, water activity and density are measured with Omix Plus and imported automatically, without typing or the typos that come with it. If you roast samples, you can link each roasted coffee back to the lot it came from, and it brings the crop information with it.",
          exporter: "Keep a record for each lot with its crop information and the moisture, water activity and density you measured before it shipped. Results from Omix Plus are imported automatically, without typing or typos, so the record your buyer opens matches what the device measured.",
          importer: "Add the crop information for each lot and measure it on arrival. Moisture, water activity and density from Omix Plus are imported automatically, without typing or typos. When you roast it, link the roasted coffee back to the green lot and the crop information comes with it.",
          q:        "Moisture, water activity, density, screen size and color from Omix Plus are imported automatically into the coffee's record, without typing or typos, next to its origin, variety and process.",
          roaster:  "Measure density and moisture with Omix Plus before the roast and import them into the coffee's record, to help you set the charge temperature. Link each roast back to its green coffee and the crop information comes with it.",
          home:     "Measure moisture and density before the roast and add them to the coffee's record, to help you set the charge temperature. Link each roasted batch back to its green coffee and the crop information comes with it.",
          bar:      "Keep each coffee you brew in the Bean Manager with its origin, process and roast date. When your roaster shares their record with you, it includes the green data and roast color they measured.",
          teach:    "Students create a record for each coffee, add its crop information and the measurements they take, entered by hand or imported from a device. Everything they do with that coffee afterward can be linked back to it.",
          comp:     "Origin, process, green data and roast color in one place, with every practice brew linked to the coffee you used.",
          default:  "Create a record for each coffee with its crop information, and import the measurements from your devices without typing."
        }
      },

      field: {
        tool: { icon: "particle-analyzer", name: "Particle Analyzer", sub: "Grind size distribution" },
        h: {
          bar:     "See the shape of your grind.",
          teach:   "Show a class what a grind really looks like.",
          comp:    "Set up the same grind on any grinder.",
          default: "See the shape of your grind."
        },
        b: {
          bar:     "Spread your grounds on the Particle Analysis Coaster or measure them with Omni, and the Particle Analyzer shows the full distribution of particle sizes, not just a dial setting. Two grinds can share an average size and still brew differently, and the spread shows you why.",
          teach:   "The Particle Analyzer shows the full particle size distribution from a photo on the Particle Analysis Coaster or a measurement with Omni, so students see the fines and the boulders instead of a single number.",
          comp:    "Measure your grind size distribution in the Particle Analyzer, then use the Grinder Converter to find the setting that gives a similar grind on another grinder, including the one at the competition.",
          default: "The Particle Analyzer shows the full distribution of particle sizes in your grind, not just a dial setting."
        }
      },

      brew: {
        tool: { icon: "brew-recorder", name: "Brew Recorder", sub: "Record brews and follow recipes" },
        h: {
          producer: "Know how your coffee tastes before you sell it.",
          exporter: "Taste every lot before you offer it.",
          importer: "Compare offers on the same recipe.",
          q:        "Brews with the method recorded.",
          roaster:  "Brew each roast to check it.",
          home:     "Brew each batch to see how it developed.",
          cafe:     "Pour it the same way, every time.",
          hobby:    "Repeat the brew you liked.",
          teach:    "Pours a class can see and compare.",
          comp:     "Practice the routine you will brew on stage.",
          default:  "Pour it the same way, every time."
        },
        b: {
          producer: "Roast a sample, brew it, and record how you made it in the Brew Recorder, so you can describe the cup to buyers with confidence. Taste it with Moment to understand how the flavors change as it cools. When a brew tastes the way you think your coffee should, you can save it as a recipe and share it with your buyers, with the roast it was made from, as your suggestion for how to roast and brew it.",
          exporter: "Brew a sample of each lot and record it in the Brew Recorder, so your cupping notes come with how the coffee was brewed. You can save a good brew as a recipe and share it with your buyer along with the rest of the lot's data.",
          importer: "Brew each sample the same way by following one recipe in the Brew Recorder, so the differences you taste come from the coffee, not the brew. Measure the extraction with R2 Extract to back up what you taste.",
          q:        "The Brew Recorder records dose, pours and flow from Microbalance Ti, and R2 Extract measures the extraction, so a tasting result comes with exactly how the cup was made.",
          roaster:  "Brew every batch on the same recipe and measure the extraction with R2 Extract. If the cup changes, you know the roast changed, and you can link the brew to the roasted coffee to compare batches later.",
          home:     "Brew every batch the same way, record it in the Brew Recorder, and compare how the cup changes from one roast to the next.",
          cafe:     "Record a brew you like and save it as a recipe. Anyone on the bar can follow it step by step, with the scale showing how close each pour is to the target, and you can record everyone's brews to check they are consistent.",
          hobby:    "Record your brews with Microbalance Ti, save the best one as a recipe, and follow it next time with every pour guided by the scale.",
          teach:    "Every brew a student records shows the bloom, each pour and the flow rate on one chart, so you can see where a brew went off course and show the class why.",
          comp:     "Record every practice run with Microbalance Ti, measure the extraction with R2 Extract, and save the run that worked as a recipe to follow until it is second nature.",
          default:  "Record a brew you like, save it as a recipe, and follow it step by step next time."
        }
      },

      insights: {
        tool: { icon: "bean-manager", name: "Bean Manager", sub: "Bean Insights" },
        h: {
          green:   "Reopen a green lot and see every roast that came from it.",
          roast:   "Every batch from one green coffee, in one place.",
          bar:     "Every brew of a coffee, in one place.",
          teach:   "Every session a student saved, grouped by coffee.",
          comp:    "Find the practice run that worked.",
          default: "Reopen a green lot and see every roast that came from it."
        },
        b: {
          green:   "Roast color, particle distribution, brews, extraction. A roasted entry can point back at the green it came from, so the green record shows every roast that came out of it. Open any of those roasts to see the sessions saved to it in Bean Insights.",
          roast:   "Link each roast to the green it came from, and the green record lists every batch roasted from it. Open a batch to see the roast color sessions and brews saved to it in Bean Insights.",
          bar:     "Open a coffee in the Bean Manager and Bean Insights lists the brews, recipes and grind sessions saved to it, so you can pick up the dial-in when a new bag of the same coffee arrives.",
          teach:   "Bean Insights lists every brew and analysis saved to a coffee, so you can review a student's work coffee by coffee.",
          comp:    "Bean Insights lists every brew and analysis you have saved to a coffee, so you can go back to your best run and compare it with the rest.",
          default: "Roast color, particle distribution, brews, extraction. A roasted entry can point back at the green it came from, so the green record shows every roast that came out of it."
        }
      },

      share: {
        tool: { icon: "", name: "Sharing", sub: "Records, recipes and sessions" },
        h: {
          green:   "Share your data with anyone on CoffeeOS.",
          roast:   "Share a roast with the cafés you supply.",
          bar:     "Give the whole team the same recipe.",
          teach:   "Share sessions with your class.",
          comp:    "Share your routine with your coach.",
          default: "Share your data with anyone on CoffeeOS."
        },
        b: {
          green:   "Share any record, recipe or session with buyers who use CoffeeOS. They open it in their own app and can view, use and build on the data exactly as you recorded it.",
          roast:   "Share a roasted coffee with its roast data and recipes. Your customers open it in their own CoffeeOS and can brew from your recipe right away.",
          bar:     "Share recipes, grinder profiles and brew records with your team. They open them in their own CoffeeOS and can follow or adjust them right away.",
          teach:   "Share a session with every student at once, and have students share their records with you. Everyone opens the same data in their own CoffeeOS.",
          comp:    "Share your coffee, recipe and readings with a coach or teammate, who opens them in their own CoffeeOS.",
          default: "Share any record, recipe or session with people who use CoffeeOS. They open it in their own app with all of the data intact."
        }
      },

      /* Moment is a stand about twelve inches tall. The cup goes on the
         base and the head sits above it, with a downward infrared
         sensor reading the surface of the liquid. Flavor Thermometer
         only controls Moment, so the chapter is about Moment and does
         not present it as a Tool. */
      moment: {
        tool: { gear: "moment", name: "Moment", sub: "Taste by Temperature" },
        h: {
          producer: "Tell buyers how your coffee should taste.",
          exporter: "Tell buyers how each lot should taste.",
          importer: "Taste every offer at the same temperatures.",
          q:        "Taste each coffee at the same temperatures.",
          roaster:  "Tell people what to taste in your coffee.",
          home:     "Taste each batch against the same notes.",
          cafe:     "Help customers taste what you taste.",
          hobby:    "Know what to taste, and when.",
          teach:    "A tasting the whole class can follow.",
          comp:     "Taste it the same way every time.",
          default:  "Know what to taste, and when."
        },
        b: {
          producer: "Taste by Temperature. Moment follows a cup as it cools and shows which flavors to expect at each temperature. Write a profile of how your coffee tastes and share it with buyers, so they taste it the way you intended.",
          exporter: "Taste by Temperature. Moment follows a cup as it cools and shows which flavors to expect at each temperature. Write a profile for each lot and share it with buyers, so they know what to look for when they cup it.",
          importer: "Taste by Temperature. Moment follows a cup as it cools and shows which flavors to expect at each temperature, so every sample is tasted on the same stages and offers are easier to compare.",
          q:        "Taste by Temperature. Moment follows a cup as it cools and shows which flavors to expect at each temperature, so every tasting runs on the same temperature stages and results are comparable from one session to the next.",
          roaster:  "Taste by Temperature. Moment follows a cup as it cools and shows the flavors to expect at each temperature. Create a profile for each coffee you roast, and the cafés and customers drinking it know what to taste and when.",
          home:     "Taste by Temperature. Moment follows the cup as it cools and shows the flavors you expect at each temperature. Create a profile for a batch, and next time you taste the same roast against the same notes.",
          cafe:     "Taste by Temperature. Moment follows each cup as it cools and shows the flavors to expect at each temperature, from a profile you or your roaster created. Customers can rate the stages they liked, so you learn which flavors they enjoy most.",
          hobby:    "Taste by Temperature. Moment follows your cup as it cools and shows the flavors to expect at each temperature. Load a roaster's profile to follow their tasting notes, or create your own and see if you taste the same thing next time.",
          teach:    "Taste by Temperature. Moment shows the flavors to expect as the cup cools, so a class tastes the same coffee against the same notes at the same time, and each student can rate whether they agreed.",
          comp:     "Taste by Temperature. Moment paces a tasting by temperature, so you learn how your coffee changes as it cools and when it should be tasted, and two sessions a week apart are comparable.",
          default:  "Taste by Temperature. Moment follows a cup as it cools and shows which flavors to expect at each temperature."
        }
      }
    },

    hero_note: "Free on iOS and Android. No purchase necessary.",
    hero_more: "Learn more",

    close: {
      h: "Get CoffeeOS for iOS and Android.",
      b: "Free to download, with no purchase necessary to start using the Tools."
    }
  };

  /* ── Shop ───────────────────────────────────────
     The catalog page. Every group is a real collection, so the
     chips are links and the browser's back button, sharing and
     Shopify's own pagination all keep working — nothing here is a
     client-side filter pretending to be navigation.

     What changes with the reader is the order of the groups and the
     line at the top. A café does not want green analyzers first. */

  var SHOP = {
    head: {
      producer: {
        h: "The devices that check a lot before it ships.",
        n: "Moisture, water activity, density and size, and the spares that keep them working."
      },
      exporter: {
        h: "One method at origin, the same one on arrival.",
        n: "The devices both ends of a shipment can agree on."
      },
      importer: {
        h: "Check the coffee against what you were told.",
        n: "Moisture, water activity and density on arrival, from devices you can quote."
      },
      q: {
        h: "Devices that state their method and their precision.",
        n: "Everything DiFluid makes, and what each one measures."
      },
      roaster: {
        h: "The devices on either side of the roast.",
        n: "Moisture and density before the charge, color and grind after the drop."
      },
      home: {
        h: "Roast measurement that fits on a kitchen counter.",
        n: "Color, moisture and density, at home."
      },
      cafe: {
        h: "What a bar actually uses, and what it does not.",
        n: "Extraction, dose and grind, plus the spares that go missing first."
      },
      hobby: {
        h: "Everything DiFluid makes, from one device to the whole set.",
        n: "Start with extraction and grind. The green side is here when you want it."
      },
      educator: {
        h: "Devices a class can share, and the spares to keep them running.",
        n: "Everything DiFluid makes, with what it measures on the card."
      },
      default: {
        h: "Everything DiFluid makes.",
        n: "Devices, systems, spares and gift cards, grouped by the job they do."
      }
    },

    /* Group keys, in the order each reader meets them. A group named
       here but not built as a block on the section is skipped. */
    /* Row order per reader. "bundles" is not in these lists any more:
       the bundles band sits above every row and carries the whole
       collection, so a bundles row as well would be the same products
       twice on one page. Accessories sits directly after the
       instrument rows for everyone, ahead of featured and gifts,
       because a spare a customer cannot find is a sale lost on a
       product they already own. */
    order: {
      green:   ["analyzers", "roasting", "brewing", "accessories", "featured", "gifts"],
      roast:   ["roasting", "analyzers", "brewing", "accessories", "featured", "gifts"],
      bar:     ["brewing", "analyzers", "roasting", "accessories", "featured", "gifts"],
      teach:   ["analyzers", "brewing", "roasting", "accessories", "featured", "gifts"],
      default: ["featured", "analyzers", "brewing", "roasting", "accessories", "gifts"]
    },

    /* One line above whichever group was promoted to the top. */
    lead: {
      green:   "Green first. The rest is here when the lot moves on.",
      roast:   "Roast first. The green side that feeds it is directly below.",
      bar:     "The bar first. Everything upstream of the cup follows.",
      teach:   "Grouped the way a syllabus runs.",
      default: ""
    },

    /* ── What the thing is for, in the reader's words ──────────
       Every instrument DiFluid makes is a black cylinder or a black
       slab photographed on wood, and half of them appear under two
       categories. A card that carries a name and a price asks the
       reader to already know the range.

       So the line above the name answers "what is this for", before
       the name answers "what is it called" and the spec answers "how
       well". Named by the measurement, never by an abstraction: a
       card that says "coffee data" has told nobody anything.

       Keyed by product handle. A handle with no line here simply has
       no line on the card — the deck is not a place to guess. The
       product's own custom.job metafield beats this, so a line can be
       fixed in Shopify without touching the theme. */
    jobs: {
      /* Green and roast */
      "difluid-omix":                          "Moisture, water activity, density",
      "omix-coffee-water-activity-moisture-density-size-meter":
                                               "Moisture, water activity, density",
      "coffmeter-m1":                          "Moisture and density",
      "coffmeter-w1":                          "Water activity",
      "coffmeter-a1-by-difluid":               "Roast color, one value",
      "omni":                                  "Roast color and grind size",
      "difluid-omniflux-live-coffee-roasting-color-tracker":
                                               "Roast by Color, live in the drum",
      "difluid-airwave":                       "Roast smoke, cleared",
      "difluid-airwave-sample-unit":           "Roast smoke, sample roaster",

      /* Brew */
      "r2-extract":                            "Extraction, from one drop",
      "r2-extract-refurbished":                "Extraction, from one drop",
      "difluid-r2-pp-brix-refractometer":      "Brix, in cherry and pulp",
      "difluid-r2-pu-brix-refractometer":      "Brix, general purpose",
      "microbalance":                          "Dose, yield and flow rate",
      "microbalance-ti":                       "Dose, yield and flow rate",
      "difluid-particle-analysis-coaster":     "Grind size, on the counter",
      "difluid-moment-coffee-flavor-reference-thermometer":
                                               "Taste by Temperature, as the cup cools",

      /* Benches sold as one */
      "brew-control-system-microbalance-r2-extract":
                                               "Scale and refractometer, together",
      "brew-control-system-ti-microbalance-ti-r2-extract":
                                               "Scale and refractometer, together",
      "ultimate-coffee-brew-control-system-omni-microbalance-r2-extract":
                                               "Grind, dose and extraction",
      "ultimate-coffee-brew-control-system-omni-microbalance-ti-r2-extract":
                                               "Grind, dose and extraction",
      "ultimate-brew-control-system-white-precision-bundle":
                                               "Grind, dose and extraction",
      "ultimate-brew-control-system-white-colorguard-bundle":
                                               "Grind, dose, extraction and color",
      "sensory-brew-control-system":           "Dose, extraction, grind, temperature",
      "roast-control-system-core-omix-plus-omniflux":
                                               "Green measurements and roast color",
      "roast-control-system-flux":             "Roast color and smoke",
      "roast-control-system-omix-plus-airwave": "Green measurements and smoke",
      "roast-control-system-ultimate-omix-plus-omniflux-airwave":
                                               "Green in, Roast by Color, smoke out",
      "roast-validation-system-coffmeter-a1-m1-w1-microbalance-ti-r2-extract-particle-analysis-coaster":
                                               "Green in, roast out, both checked",
      "pro-roaster-barista-bundle-omix-r2-microbalance-ti":
                                               "Green in, roast out, both checked",
      "roaster-barista-system-coffmeter-w1-m1-a1":
                                               "Water activity, moisture, color, grind",
      "roastery-starter-kit-coffmeter-a1-m1":  "Color and moisture, to start",

      /* The parts that keep the instruments working */
      "colorguard":                            "Calibrates roast color",
      "omni-roast-color-testing-dish":         "Spare Omni sample dish",
      "omni-electric-coffee-distributor":      "Levels an Omni sample",
      "omix-bean-sweeper-sample-prep-module":  "Loads an Omix sample",
      "airwave-replacement-catalyst":          "AirWave catalyst, spare",
      "airwave-heating-coil-replacement":      "AirWave heating coil, spare",
      "pad":                                   "Keeps brewer heat off the scale",
      "bcs-package-box":                       "Case for the brewing pair",
      "titanium-cupping-spoon":                "Cupping spoon",
      "reusable-conical-coffee-filter-cup-for-hand-brewing-resin-dripper-strainer":
                                               "Reusable dripper",

      /* Not instruments */
      "coffee-spectrum-magazine":              "Reading",
      "turning-coffee-data-into-better-decisions-tote": "Tote bag",
      "digitize-fluid-hemp-t-shirt":           "T-shirt",
      "difluid-gift-cards-copy":               "Gift card"
    }
  };

  /* ── Systems ────────────────────────────────────
     A system is a closed loop, not a discount. Each family below is
     one loop; the rungs inside it are the same loop with more of it
     measured. Prices, availability and the sum of the parts come
     from Shopify in sections/os-systems.liquid — nothing here is a
     number, so nothing here goes stale. */

  var SYSTEMS = {
    slug: "systems",
    eyebrow: "Systems",
    title: "Systems",
    hero: "os-cos-ecosystem",
    heroAlt: "DiFluid devices on a table, from green coffee through to the cup",

    head: {
      producer: {
        h: "Ship a lot with its measurements attached.",
        n: "Moisture, water activity and color measured at the mill and shared with your buyers, so they can see the data behind the coffee."
      },
      exporter: {
        h: "One method at both ends of the shipment.",
        n: "The same devices in the sample room and on arrival, so any difference you find comes from the coffee, not the method."
      },
      importer: {
        h: "Check the offer against the coffee.",
        n: "Moisture, water activity and density on arrival, then a brew on a fixed recipe, all saved to the same lot."
      },
      q: {
        h: "Green, roast and cup, on one bean.",
        n: "Measurements with a stated precision, and a tasting you can run the same way twice."
      },
      roaster: {
        h: "Green in, roast color through, cup out.",
        n: "Green data, roast color and the cup, linked to the same coffee."
      },
      home: {
        h: "Roastery measurements on a kitchen counter.",
        n: "The measurements a production roastery takes, at the scale you actually roast."
      },
      cafe: {
        h: "Make the cup you already like, again.",
        n: "Dose, yield, extraction and grind, recorded, so you can repeat your best brews."
      },
      hobby: {
        h: "Start with a scale and a refractometer.",
        n: "Weight and extraction first. Everything you add later works with the same app."
      },
      educator: {
        h: "Devices a class can share, results per student.",
        n: "Each student's results, shared with you in CoffeeOS."
      }
    },

    intro:
      "A system is the smallest set of devices that closes one loop: " +
      "measure something, record it, change one thing, measure again. " +
      "CoffeeOS keeps both readings with the same coffee, so you can see " +
      "what the change did. Start with the loop you care about. Every " +
      "system works with the same app, so a system you add later builds " +
      "on everything you have already measured.",

    /* The three beats under the intro. */
    how: [
      {
        t: "Measure",
        n: "Devices with a stated method and precision, so a reading holds up when you quote it."
      },
      {
        t: "Record",
        n: "Save every reading to the bean in CoffeeOS, free on iOS and Android, with every other reading that bean has had."
      },
      {
        t: "Change",
        n: "Change one variable and measure again, and the record shows what it did."
      }
    ],

    /* Families. `tiers` are rungs, in the order they should be read.
       `h` is the Shopify handle; `alt` is a second handle for the
       same rung in another finish. */
    families: [
      {
        id: "brew",
        name: "Brew Control",
        loop: "Grind, dose, extraction, taste",
        lead:
          "The loop between the grinder and the cup. Weight and flow going in, " +
          "dissolved solids coming out, and the arithmetic between them done for you. " +
          "This is the cheapest place to start, and the loop most people can close in a week.",
        why: {
          green: "Taste your own lot the way the person buying it will.",
          q: "Dose, beverage mass and TDS: every input the extraction calculation needs.",
          roast: "The half of the loop that tells you whether the roast actually developed, rather than merely darkened.",
          bar: "The two devices a bar uses every day.",
          hobby: "Two devices, one app, and you can see whether the change you made changed the cup.",
          teach: "Scale and refractometer on one purchase order."
        },
        tiers: [
          {
            h: "brew-control-system-ti-microbalance-ti-r2-extract",
            adds:
              "Weight, time and flow rate during the brew; total dissolved solids after it. " +
              "Together those give extraction yield, which is the figure everything else on this page builds on."
          },
          {
            h: "sensory-brew-control-system",
            adds:
              "Adds Moment and the Particle Analysis Coaster. Taste by Temperature: Moment shows " +
              "the flavors to expect at each temperature as the cup cools, so a cupping runs the same " +
              "way twice. Spread grounds on the Particle Analysis Coaster and photograph them to measure grind size in CoffeeOS."
          },
          {
            h: "ultimate-coffee-brew-control-system-omni-microbalance-ti-r2-extract",
            adds:
              "Adds Omni. Grind becomes a distribution (D10, D50, D90 and how even the grind is), and you " +
              "can save roast color to the same bean as the brew. Add ColorGuard to keep color readings " +
              "matched between devices, and Omni Volto when leveling samples by hand is the slow step."
          }
        ]
      },
      {
        id: "bean",
        name: "Bean Analysis",
        loop: "Moisture, density, water activity, color",
        lead:
          "The loop before the roast. Single-purpose devices instead of one combined unit, " +
          "which costs less when several people measure at once or in two places.",
        why: {
          green: "Moisture, water activity and density, the figures a buyer asks for, at a price that makes sense for a mill.",
          q: "Water activity, moisture, density and Agtron color, the attributes CVA describes.",
          roast: "Density and moisture help you set the charge. Roast color shows where the roast ended. Both, before you commit a batch.",
          bar: "The devices behind the moisture and density on a green spec sheet, if you buy green yourself.",
          teach: "Separate devices, so several students can measure at once."
        },
        tiers: [
          {
            h: "roastery-starter-kit-coffmeter-a1-m1",
            adds:
              "Roast color, plus the two green measurements that help you set a charge temperature: moisture and density. " +
              "The smallest set that shows what you bought and where the roast ended."
          },
          {
            h: "roaster-barista-system-coffmeter-w1-m1-a1",
            adds:
              "Adds CoffMeter W1 for water activity, which tells you how the lot will store, " +
              "and the Particle Analysis Coaster for grind size."
          }
        ]
      },
      {
        id: "roast",
        name: "Roast Control",
        loop: "Green measurements, live color, exhaust",
        lead:
          "Roast by Color. OmniFlux measures roast color through the roaster window and draws it " +
          "as a curve. Omix Plus measures the green before it goes in. AirWave removes the smoke and, " +
          "paired with OmniFlux, adjusts its airflow to each stage of the roast automatically.",
        why: {
          green: "Send a sample roast with its color curve, not only a tasting note.",
          q: "A roast that can be described by its trajectory, not just its endpoint.",
          roast: "The drop stops being a decision you make from memory and smell alone.",
          bar: "The setup behind a roaster who delivers the same roast every week.",
          teach: "Roast development, drawn live, in front of the room."
        },
        tiers: [
          {
            h: "roast-control-system-flux",
            adds:
              "OmniFlux and AirWave. Roast by Color through the glass, and the exhaust cleaned at the " +
              "catalyst rather than vented into the room. That is what makes roasting indoors workable."
          },
          {
            h: "roast-control-system-core-omix-plus-omniflux",
            adds:
              "Omix Plus and OmniFlux. What went in, measured on six sample types, and Roast by Color " +
              "while it happened. The measurement pair, without the air handling."
          },
          {
            h: "roast-control-system-omix-plus-airwave",
            adds:
              "Omix Plus and AirWave. Green analysis and clean exhaust, for a roastery whose color " +
              "measurement already happens after the drop."
          },
          {
            h: "roast-control-system-ultimate-omix-plus-omniflux-airwave",
            adds:
              "All three. Green in, Roast by Color, smoke out: the complete roasting loop, " +
              "and the configuration AirWave's automatic mode was built for."
          }
        ]
      },
      {
        id: "validate",
        name: "Roast Validation",
        loop: "Green in, roast out, cup checked",
        lead:
          "Both loops at once. Green, roast and cup measurements linked to the same coffee, " +
          "so you can trace a change in the cup back to a change in the roast.",
        why: {
          green: "What the lot measured, how it roasted and how it brewed, ready to share with a buyer.",
          q: "Every green measurement and every extraction figure, linked to the same coffee.",
          roast: "The loop closed. Change the profile, and the cup measurement tells you whether it worked.",
          bar: "What a roastery uses to explain what changed in last week's bag.",
          teach: "Green grading, roast analysis and extraction, taught from one set of devices."
        },
        tiers: [
          {
            h: "roast-validation-system-coffmeter-a1-m1-w1-microbalance-ti-r2-extract-particle-analysis-coaster",
            adds:
              "The three CoffMeters for the green side, the brewing pair (Microbalance Ti and R2 Extract) for the cup side, " +
              "and a Particle Analysis Coaster for the grind between them."
          },
          {
            h: "pro-roaster-barista-bundle-omix-r2-microbalance-ti",
            adds:
              "Omix Plus in place of the three CoffMeters, measuring six sample types and a full color " +
              "distribution from one device, with the same brewing pair, plus the Bean Sweeper " +
              "so every sample is the same volume."
          }
        ]
      }
    ],

    /* Where a given role should start. The handle names a system on
       this page; the line says why that rung and not another. */
    start: {
      producer: {
        h: "roaster-barista-system-coffmeter-w1-m1-a1",
        n: "Water activity and moisture are the figures buyers ask about first. Measure them at the mill and share them with every offer."
      },
      exporter: {
        h: "roaster-barista-system-coffmeter-w1-m1-a1",
        n: "Single-purpose devices are affordable enough to keep at origin, so both ends of a shipment can measure the same way."
      },
      importer: {
        h: "roast-validation-system-coffmeter-a1-m1-w1-microbalance-ti-r2-extract-particle-analysis-coaster",
        n: "Moisture, water activity and density on arrival, then a brew on a fixed recipe: enough to check an offer against the coffee that turned up."
      },
      q: {
        h: "sensory-brew-control-system",
        n: "Moment shows the whole table what to taste at each temperature, so a tasting runs the same way every time, and the extraction figures underneath it are ones you can quote."
      },
      roaster: {
        h: "roast-control-system-core-omix-plus-omniflux",
        n: "Moisture and density in, and Roast by Color through the roast. Add AirWave when the room, or the neighbors, becomes the problem."
      },
      home: {
        h: "roastery-starter-kit-coffmeter-a1-m1",
        n: "Color, moisture and density change what you do with the next batch. Two devices on a kitchen counter."
      },
      cafe: {
        h: "brew-control-system-ti-microbalance-ti-r2-extract",
        n: "Dose, yield and extraction on the bar. Add Omni when you need to measure the grind."
      },
      hobby: {
        h: "brew-control-system-ti-microbalance-ti-r2-extract",
        n: "A scale and a refractometer, so you can compare every brew you make."
      },
      educator: {
        h: "ultimate-coffee-brew-control-system-omni-microbalance-ti-r2-extract",
        n: "Grind distribution and extraction from one set of devices, so fines, boulders and yield can be taught in the same session."
      }
    },

    /* The CoffeeOS band at the foot of the page, painted by
       os-systems.js. It is written for someone who has never heard
       of CoffeeOS: one sentence saying what it is, then one coffee
       followed through four real screens, chosen by role.

       `steps` is the library of screens. `flow` names four of them
       per role or family, in order. `walk` is the line above the
       steps. Resolution is the same as everywhere else: role, then
       family, then default. */
    cos: {
      eyebrow: "CoffeeOS · free on iOS and Android",
      def:
        "Every device on this page connects to one free app. Save each result to the " +
        "coffee it came from, and every reading that coffee has ever had is in one place.",
      head: {
        producer: "Every lot, with its measurements.",
        exporter: "One lot page, opened at both ends of the shipment.",
        importer: "Offer sample and arrival, on the same page.",
        q: "Green, roast and cup, on a single page.",
        roaster: "Every batch keeps its green, its color and its cup.",
        home: "A roast log with no handwriting in it.",
        cafe: "Yesterday’s best brew, ready for today’s bar.",
        hobby: "Every brew you make, ready to compare next week.",
        educator: "Show a class where each figure comes from.",
        default: "One coffee, every reading, one page."
      },
      walk: {
        green: "Follow one lot",
        q: "Follow one sample",
        roast: "Follow one batch",
        bar: "Follow one brew",
        teach: "Follow one brew through a lesson",
        default: "Follow one coffee"
      },
      flow: {
        green: ["lot", "physicals", "colour", "greens"],
        q: ["physicals", "colour", "pour", "extract"],
        roast: ["physicals", "colour", "extract", "roasts"],
        bar: ["pour", "extract", "grind", "recipe"],
        teach: ["pour", "extract", "grind", "colour"],
        default: ["physicals", "colour", "extract", "roasts"]
      },
      steps: {
        lot: {
          img: "os-app-link-4", tag: "Bean Manager",
          h: "A record for the lot",
          n: "Origin, process, altitude and variety, entered by hand or read from a photo of the bag label."
        },
        physicals: {
          img: "os-app-bean-4", tag: "Omix Plus",
          h: "Moisture, density and size, imported",
          n: "Moisture, water activity, bulk and true density and screen size, imported from Omix Plus without typing."
        },
        colour: {
          img: "os-app-roastdelta", tag: "Roast Color Analyzer",
          h: "Roast color, surface and center",
          n: "Whole bean and grounds measured apart. Here the grounds read 18 Agtron lighter, so the center of the bean is less developed than its surface."
        },
        pour: {
          img: "os-app-record", tag: "Microbalance Ti · Brew Recorder",
          h: "Every pour, drawn as it happens",
          n: "Weight and flow second by second, with the bloom and each pour marked on the curve."
        },
        extract: {
          img: "os-app-tds", tag: "R2 Extract · Brew Controller",
          h: "Extraction, calculated",
          n: "R2 Extract sends the TDS, and with the dose and water already recorded, CoffeeOS calculates the extraction."
        },
        grind: {
          img: "os-app-d50", tag: "Omni · Particle Analyzer",
          h: "The grind, in microns",
          n: "D10, D50 and D90 from a spoonful of grounds: a grind described by size rather than by a number on a dial."
        },
        recipe: {
          img: "os-app-bean-6", tag: "Bean Manager",
          h: "The recipe, linked to the coffee",
          n: "Ratio, grind size, roast color, water temperature and every pour, saved to the bean it was built for."
        },
        greens: {
          img: "os-app-link-2", tag: "Bean Manager",
          h: "The whole season, lot by lot",
          n: "Every lot you hold with its moisture, searchable by process, variety and altitude."
        },
        roasts: {
          img: "os-app-link-3", tag: "Bean Manager",
          h: "Every batch, with its Agtron",
          n: "Each roast listed with its color and linked back to the green lot it came from."
        }
      },
      facts: ["Free", "iOS and Android", "A Tool for each coffee task", "Share any coffee with others on CoffeeOS"],
      cta: "See what CoffeeOS does"
    },

    learn: [
      { label: "Taking control of your brew", url: "/pages/brew-control-system-intro", note: "What the brew loop actually measures" },
      { label: "Understanding color measurement", url: "/blogs/difluid-resources/understanding-color-measurement-in-coffee", note: "Before you buy a color meter" },
      { label: "The five green measurements", url: "/blogs/difluid-resources/5-green-bean-metrics-that-quietly-decide-your-cup-score", note: "Moisture, water activity, density, screen size" }
    ],

    next: [
      { slug: "shop", label: "Everything", img: "os-brew-hero" },
      { slug: "coffeeos", label: "CoffeeOS", img: "os-app-record" },
      { slug: "learn", label: "Learn", img: "os-farm-hero" }
    ]
  };

  function pick(map, role) {
    if (!map) return "";
    if (map[role]) return map[role];
    var fam = FAMILY[role];
    if (fam && map[fam]) return map[fam];
    return map.default || "";
  }

  /* os.js finds a room by looking it up in STAGES, so Systems has to
     be in there as well as on its own key — the section carries
     data-os-stage="systems", and without this line paintStage falls
     out early and the hero, headline, intro, CoffeeOS band, Learn
     and Keep going all stay empty. It has no `tools` array, which is
     fine: paintStage only touches that when the page has a
     [data-os-tools] element, and this one does not. */

  /* ── The brewing competitor ──────────────────────────
     A tenth role, and its own family: comp.

     Competition is repeatability under a clock. Everything this
     persona reads has the same spine — a practice run only counts if
     it was measured, and the routine that goes on stage is the one
     the record says worked. They brew and they roast, so they meet
     Moment, R2 Extract, Omni and Omix Plus first.

     Kept in one block rather than a key in forty-five places, so the
     persona can be read, edited or removed in one sitting. Everything
     above is untouched; this only adds. */
  FAMILY.competitor = "comp";

  HOME.head.competitor = {
    h: "Make the same cup twice, then twice more.",
    n: "Every practice run is measured, so what you take on stage is a proven recipe."
  };

  HOME.kit.competitor = {
    h: "sensory-bcs",
    lead:
      "Microbalance Ti records every pour of every practice run and R2 Extract measures " +
      "the extraction, so you can see what made one run better than another and repeat " +
      "it on stage. Use the Particle Analysis Coaster to confirm your grind size before " +
      "each run. Moment tracks the temperature of the cup as it cools, so you learn how " +
      "your coffee changes and when it should be tasted.",
    gear: ["microbalance-ti", "r2-extract", "moment", "coaster"],
    tools: ["brew-recorder", "brew-controller", "particle-analyzer", "flavor-thermometer", "notes-manager"]
  };

  /* ── Farm & green ── */
  STAGES.farm.head.competitor = {
    h: "Know the lot before you buy.",
    n: "Moisture, water activity and density on a competition coffee, before you buy the only bag of it you will get."
  };

  /* ── Roast ── */
  STAGES.roast.head.competitor = {
    h: "The same roast on the day.",
    n: "Color after the drop, and the curve that got there, so a practice roast and the one you compete on are the same roast."
  };

  /* ── Brew ── */
  STAGES.brew.head.competitor = {
    h: "One attempt on the day.",
    n: "Everything before it is practice, and practice only counts if it was measured."
  };
  STAGES.brew.tools[0].why.comp =
    "Dose, yield and the flow of the pour, recorded: the parts of a routine that drift first under pressure.";
  STAGES.brew.tools[1].why.comp =
    "Extraction on every practice run, so the recipe you present is one you can put a number behind.";
  STAGES.brew.tools[3].why.comp =
    "Grind held still between practice and the stage, so the thing you changed is the thing you meant to change.";

  /* ── Systems ── */
  SYSTEMS.head.competitor = {
    h: "Practice that survives the day.",
    n: "Green in, roast out, cup measured, linked to one coffee, so a routine is repeatable."
  };
  SYSTEMS.families[0].why.comp =
    "The loop a routine is built on: dose, extraction and grind, measured the same way at every practice.";
  SYSTEMS.families[1].why.comp =
    "Moisture, water activity and density on a competition lot, taken by you rather than quoted from whoever sold it.";
  SYSTEMS.families[2].why.comp =
    "A sample roast and the competition roast, matched by color rather than by memory.";
  SYSTEMS.families[3].why.comp =
    "Green, roast and cup in one record, which is most of what a judge is asking you to explain.";
  SYSTEMS.cos.head.competitor = "The practice run that worked, recorded in full.";
  SYSTEMS.cos.walk.comp = "Follow one practice run";
  SYSTEMS.cos.flow.comp = ["pour", "extract", "grind", "recipe"];
  SYSTEMS.start.competitor = {
    /* The Systems page keys by Shopify handle; the homepage kit keys
       by catalog shortname. Same bundle, two different keys. */
    h: "sensory-brew-control-system",
    n: "Dose, extraction, grind and a tasting run the same way every time. It is the loop a routine is built on."
  };

  /* ── Shop ── */
  SHOP.head.competitor = {
    h: "The devices a routine is built on.",
    n: "Brewing and roasting, and the tasting that ties them together."
  };
  SHOP.order.comp = ["brewing", "analyzers", "roasting", "accessories", "featured", "gifts"];
  SHOP.lead.comp = "Brewing first, then what the roast did, then everything that keeps both steady.";

  /* ── Lines the sections shipped with ──────────────────────
     Every OS section was added to the theme with its text fields
     already filled from the preset, and a theme-editor field always
     beats this file. So when the copy here changes, a field that
     was never edited would go on showing the old line for ever.

     This is every text line any OS section preset has ever carried
     (the homepage list above is the same idea, kept for the record).
     A field still holding one of them word for word was never
     touched, so it is dropped and the line in this file shows.
     Anything actually edited in the theme editor still wins. */
  var RETIRED = [
    "/pages/coffeeos",
    "0 to 150 Agtron · ±0.5 · 3 s",
    "0 to 35, 0 to 55 or 0 to 95 °Brix",
    "0.05 to 0.99 aw · ±0.02 · ~2 min",
    "0.1 g · 3,000 g · flow rate",
    "1 to 150 Agtron · ±0.1 · ~5 g",
    "3000 g · flow rate",
    "A brew bench for the sample room.",
    "A cheap second opinion on a sample roast.",
    "A class tastes the same coffee against the same words, and everyone registers whether they agreed.",
    "A color check for a shop that needs one value and not the spread.",
    "A color check for a shop that needs the number and not the spread.",
    "A complete extraction bench for one purchase order.",
    "A figure with a stated precision, which is what makes it quotable.",
    "A fixed recipe and a TDS reading show whether the batch developed, and every result is saved to the batch it came from.",
    "A live color curve to send beside the sample roast you shipped.",
    "A live color trace to put beside the sample roast you shipped.",
    "A live color trace to put beside the sample roast you shipped.",
    "A reading you can send.",
    "A recorded brew carries further than an adjective.",
    "A recorded brew with its TDS carries further than an adjective. Attach it to the lot and it travels with the offer.",
    "A ripeness figure from the cherry, before any of the green measurements exist.",
    "A roast curve and a color number in one session.",
    "A roast curve and an Agtron reading from one session.",
    "A roast curve and an Agtron reading.",
    "A roast is a sequence of decisions you have to be able to make again. These instruments measure the bean before the charge, during the roast and after the drop, and every reading is saved to the coffee it came from in CoffeeOS.",
    "A roast is a sequence of decisions you have to be able to make again. These read the bean before the charge, while the drum is turning, and after the drop — and write each reading down against the coffee it came from.",
    "A roast report with the sample.",
    "A roasting class in a room that was never built for one.",
    "A sample roast with numbers attached travels further than a tasting note.",
    "A second instrument for cross-checks, cheap enough to leave at origin.",
    "A second water activity reading at origin, without buying a second Omix Plus.",
    "A single Agtron color number. No distribution.",
    "A single Agtron value, for a fast check between samples.",
    "A single Agtron value. No distribution.",
    "A tasting run at the same temperature stages every time.",
    "A1, Omni, OmniFlux, Omix Plus",
    "Accessories",
    "Affordable enough for one per workstation.",
    "Affordable enough that a class can have several going at once.",
    "Agtron color, after the roast or live through the drum window.",
    "Agtron color 0–150 to ±0.1, and particle distribution, from about five grams.",
    "Agtron color, on the bench or through the drum window.",
    "AirWave",
    "AirWave breaks roasting smoke down into carbon dioxide and water vapor with a low-temperature catalyst, so a roaster up to 1 kg (800 g on 120 V) can run indoors without a duct to the street. Paired with OmniFlux it adjusts its own airflow to each stage of the roast, so you set it once and roast.",
    "Analyzers",
    "Ask for the Brix of the cherries, and the story of your coffee starts at the tree.",
    "Ask your producer for the Brix of the cherries your lot came from.",
    "Ask your roaster a question they can answer.",
    "Ask your roaster for the color curve of the roast on your shelf.",
    "At origin the loop is short: measure, file it against the lot, send the lot on with its file.",
    "At origin the loop is short: measure, save it to the lot, send the lot on with its record.",
    "Behind the numbers your roaster quotes.",
    "Behind the shelf-life claim on a bag.",
    "Best sellers",
    "Both ends of the roast",
    "Brew Controller puts dose, yield and TDS on the chart and does the extraction arithmetic.",
    "Brew Controller shows extraction as a live number, which is the hard part to teach on a slide.",
    "Brew Recorder first. Record every practice run and the good one stops being a story.",
    "Brew control",
    "Brew control systems",
    "Brew every batch on one fixed recipe and measure the extraction. If the cup changed, the roast changed, and both are on the same bean in CoffeeOS.",
    "Brew it like your buyer.",
    "Brew it to check development.",
    "Brew scale to 3000 g, with flow rate.",
    "Brew scale with a 3,000 g capacity. Records dose, yield and the flow of the pour.",
    "Brew the lot on the recipe your customers use, measure the extraction, and send the result with the sample.",
    "Brewing and roasting, and the tasting that ties them together.",
    "Brewing is where a coffee stops being a lot number and becomes a cup. These measure what went in, what came out, and what the grinder actually did — which is usually the part nobody checked.",
    "Brewing is where a coffee stops being a lot number and becomes a cup. These measure what went in, what came out, and what the grinder actually did. That is usually the part nobody checked.",
    "Brewing scales",
    "Brix at the tree and at the wet mill, so ripeness is a figure you can set a standard on.",
    "Build the bean record at origin. It is the thing you attach to the offer.",
    "Bundles",
    "Bundles and kits",
    "Burns the smoke off roast exhaust, so roasting works in a room that could not take it otherwise.",
    "Burrs, retention, setting history",
    "Calibration samples, cases, pads and spares.",
    "Cases",
    "Catalytic smoke handling for roasters around the one-kilogram class.",
    "Charge temperature follows density and moisture. Both are measurable.",
    "Cheap enough for one per workstation.",
    "Cheap enough that a class can have several going at once.",
    "Check it against what the seller told you.",
    "Check the coffee against what you were told.",
    "Check the roast by brewing it.",
    "Check what you were told.",
    "Chilled mirror, named as the method, so the water activity figure survives being quoted in a report.",
    "CoffMeter A1 measures the roast color of whole beans or grounds in three seconds, 0 to 150 Agtron, ±0.5. It gives a single value rather than a distribution, saved to the batch in CoffeeOS like every other reading.",
    "CoffMeter M1 measures moisture to ±0.2% and density to ±1 g/L in under two seconds, on green, parchment, dried cherries or roasted coffee. The figures a buyer asks for first, saved to the lot in CoffeeOS.",
    "CoffMeter W1 measures water activity from 0.05 to 0.99 aw, ±0.02, in about two minutes. Water activity is the figure that predicts how a lot will store, and it is saved to the lot in CoffeeOS beside the moisture.",
    "Color after the drop, and the curve that got there, so a practice roast and the one you compete on are the same roast.",
    "Color and development, recorded per batch, on your phone.",
    "Color and distribution, each reported with a standard deviation.",
    "Color as a measurement, not a judgment.",
    "Color as a measurement.",
    "Color on a time axis rather than one end point.",
    "Color on a time axis rather than one end-point.",
    "Color ±0.1 Agtron · particles from ~5 g",
    "Color, moisture and size, the attributes CVA describes.",
    "Color, moisture, water activity, density, particle size and TDS.",
    "Color, roast delta and spread, saved per batch, so you can show what last month's roast measured.",
    "Color after the drop, and the curve that got there, so a practice roast and the one you compete on are the same roast.",
    "Color and development, recorded per batch, on a phone rather than a bench.",
    "Color and distribution, each reported with a standard deviation.",
    "Color as a measurement, not a judgement.",
    "Color on a time axis rather than one end-point.",
    "Color ±0.1 Agtron · particles from ~5 g",
    "Color, and the delta between whole bean and ground, recorded against the lot.",
    "Color, delta and spread against the bean — so last month's roast is still an argument you can win.",
    "Color, moisture, water activity, density, particle size and TDS.",
    "Compare color meters",
    "Compare color meters",
    "Compare offers on one recipe.",
    "Complete benches, priced together.",
    "Complete systems, priced together.",
    "Confirm the coffee you received.",
    "Convert a setting between two grinders from their distributions, rather than a chart somebody posted.",
    "D10 / D50 / D90 and spread",
    "D10 / D50 / D90 · roast color ±0.1 Agtron",
    "D10, D50, D90 and the standard deviation, reported.",
    "Density",
    "Density and moisture help you set the charge temperature and judge how long the drying phase will take.",
    "Density and moisture help you set the charge temperature, and both come off the same sample you are about to roast.",
    "Density and moisture help you set the charge temperature. Both are measurable, and both are saved to the lot.",
    "Density and moisture help you set the charge temperature. Both are measurable.",
    "Density and moisture help you set the charge temperature. Guessing them costs a batch.",
    "Density and moisture off one sample, before you pick a charge temperature.",
    "Density and moisture set the charge temperature, and both come off the same sample you are about to roast.",
    "Density and moisture set the charge. Guessing them costs a batch.",
    "Density sets the charge; moisture tells you how long the drying phase will take.",
    "Desk-scale instruments, and a session that ends in a file.",
    "Development stops being a story and becomes something the room can watch.",
    "Dial in from the record, not from memory.",
    "Dose and beverage mass, recorded, so the yield calculation has inputs.",
    "Dose and yield to a tenth of a gram, and a flow trace that shows where your pour sped up.",
    "Dose and yield, and a flow trace that shows where a pour went sideways.",
    "Dose, grind, yield and extraction, kept per bean, so the bar is not rebuilding the recipe each morning.",
    "Dose, yield and TDS — every input the extraction calculation needs.",
    "Dose, yield and TDS: every input the extraction calculation needs.",
    "Dose, yield and flow rate.",
    "Dose, yield and the flow of the pour, recorded — the parts of a routine that drift first under pressure.",
    "Dose, yield and the flow of the pour, recorded: the parts of a routine that drift first under pressure.",
    "Dose, yield, TDS and what each one tells you about the cup",
    "Each Tool records what it measured, how, and with what spread. That is what makes a figure quotable.",
    "Enough to close the loop between the roast and the cup.",
    "Enough to tell a light roast from a medium one, in writing.",
    "Enter dose, yield and TDS in the Brew Controller and it plots the brew and calculates the extraction.",
    "Every cup goes out with its own tasting note, and customers rate what they tasted.",
    "Every measurement is saved to the bean, and the bean goes out with the offer.",
    "Every measurement lands on the bean record, and the bean record travels with the offer.",
    "Every measurement, explained",
    "Every practice run is measured, so what you take on stage is a proven recipe rather than a good day.",
    "Every practice run, kept.",
    "Every roast, on the record.",
    "Every screen, in order",
    "Every student session produces a record with the method attached, not a photo of a screen.",
    "Every student session produces a record with the method attached, not a screenshot.",
    "Every student's brew ends as a file.",
    "Every student's brew is recorded and shared with you in CoffeeOS.",
    "Everything DiFluid makes, from one instrument to the whole set.",
    "Everything DiFluid makes, from one shelf to a full bench.",
    "Everything DiFluid makes, with what it measures on the card.",
    "Everything before it is practice, and practice only counts if it was measured.",
    "Extraction and grind, kept per bean and per bag.",
    "Extraction on every practice run, so the recipe you present is one you can put a number behind.",
    "Extraction on the bar, between orders, in about four seconds.",
    "Extraction on the bar, between orders, without walking to a bench.",
    "Extraction stops being a formula on a slide.",
    "Extraction taught with a live number.",
    "Extraction tells you whether the bean actually developed.",
    "Extraction yield you can defend.",
    "Extraction, dose and grind — plus the spares that go missing first.",
    "Extras",
    "Extras and add-ons",
    "Fast enough that you will actually check between practice roasts.",
    "Find out what your burrs are actually doing.",
    "Fines and boulders, on a curve, in front of the room.",
    "Five grams, several tests, the average kept.",
    "Five physicals in one pass, at the patio or on arrival, without booking bench time somewhere else.",
    "Five physicals off one sample, so the coffee you compete on is described by measurements you took yourself.",
    "Follow a roaster's notes through the temperature range, or write your own and see if you taste it the same way next time.",
    "For when water activity is the figure under dispute and you do not need the rest of the panel.",
    "For when you do not want to pick the instrument.",
    "Free. iOS and Android.",
    "Gift cards",
    "Green analyzers",
    "Green coffee arrives with claims attached — moisture, water activity, density, screen size, defect count. These are the instruments that let you check the claims yourself, on the patio or at the bench, and keep the number afterwards.",
    "Green coffee arrives with claims attached: moisture, water activity, density, screen size, defect count. These are the instruments that let you check those claims yourself, on the patio or on arrival, and keep every reading in CoffeeOS.",
    "Green grading a student can actually do.",
    "Green grading and roast analysis without buying twice.",
    "Green grading students can do.",
    "Green has numbers too.",
    "Green physicals before the charge, color and grind after the drop.",
    "Green physicals before the charge; color distribution after the drop.",
    "Grind held still between practice and the stage, so the thing you changed is the thing you meant to change.",
    "Grind held still, so the variable you are testing is the roast.",
    "Grinder Manager first. Once the burrs have a curve, every other number gets easier.",
    "Grinder Manager setup",
    "Ground color as a distribution, from five grams — small enough to check a roast without spending the bag you are saving.",
    "Ground color you can repeat.",
    "Grounds color as a distribution, from five grams, small enough to check a roast without spending the bag you are saving.",
    "Instruments a class can share, and spares for when they don't.",
    "Instruments a student can afford, and results they can share with you in CoffeeOS.",
    "Judge the coffee, not the roaster.",
    "Judge the coffee, not the sample roaster.",
    "Know every batch's color.",
    "Know how a lot will store before you commit to it.",
    "Know the lot before you buy.",
    "Know the lot before you commit to it.",
    "Know the lot is dry before it leaves the patio.",
    "Know the lot is dry.",
    "Know the moisture and density of a bag before you roast it, so a batch that behaved differently can be explained.",
    "Know the moisture and density of a bag, so you can see why the same recipe drifted.",
    "Know the roast color of every batch.",
    "Know what changed in the bag you were sent.",
    "Know what changed in the bag.",
    "Know what you bought before you charge it.",
    "Know what you bought.",
    "Know when the cup should be tasted, based on how it was brewed.",
    "Know whether a bag was stored well before you roast or brew it.",
    "Know why the same espresso keeps drifting.",
    "Know why today's cup tasted better than yesterday's.",
    "Learn how moisture, water activity and density are measured.",
    "Learn it, then teach it from a file.",
    "Learn it, then teach it from your students' own records.",
    "Learn the methods behind the physicals.",
    "Learn to check an offer against its paperwork.",
    "Learn to do it the same way twice, on the day.",
    "Learn to make yesterday's cup again.",
    "Learn to repeat a roast on purpose.",
    "Learn to roast by measurement, not by ear.",
    "Learn to send a reading, not an adjective.",
    "Learn what moisture and water activity mean for your lot.",
    "Learn what the numbers on your lot mean.",
    "Learn what your grinder is actually doing.",
    "Live color, in the roast",
    "Live color, in the roast",
    "Live ±1 Agtron · static ±0.1 · marks cracks",
    "Make the cup you already like, again.",
    "Make the same cup twice, then twice more.",
    "Match production to the sample.",
    "Match your production roast to the sample.",
    "Measure moisture and water activity on the drying bed, and send the results with the lot.",
    "Measure roast color, moisture and density, so you can see what changed between batches.",
    "Measure the extraction to see whether the roast developed.",
    "Measure the grind, the pour and the extraction, then taste it at the right temperature.",
    "Measure the grind, the pour and the extraction, then taste the cup at the right temperature. Change one thing at a time and see what it did.",
    "Measure the grounds, then convert the setting.",
    "Measure the lot before it ships and again when it arrives, so any difference is in the coffee, not the method.",
    "Measure them at the mill and send the results with your offer.",
    "Measure whole beans and grounds after every batch, and compare the roast delta with the sample roast.",
    "Measure whole beans and grounds from one sample in the Roast Color Analyzer, and read the roast delta between them.",
    "Measure whole beans and grounds on one instrument after every batch, and compare the roast delta with the sample roast.",
    "Measure whole beans and grounds on one instrument. The roast delta between them shows whether the inside of the bean developed as far as the outside.",
    "Measured precisely, and linked to the beans they came from.",
    "Measured, not estimated.",
    "Measures TDS in about four seconds, 0 to 30%, ±0.02%.",
    "Measures moisture and density in about two seconds.",
    "Measures moisture, water activity, density, screen size and roast color from one sample.",
    "Measures water activity, the figure that predicts how a lot will store.",
    "Microbalance Ti and R2 Extract, bought as the job rather than as parts.",
    "Microbalance Ti weighs to 0.1 g with a 3,000 g capacity and sends weight and flow rate to CoffeeOS over Bluetooth. The Brew Recorder draws the pour as a curve, marks the bloom and each pour, and saves the brew as a recipe anyone can follow.",
    "Moisture",
    "Moisture + density",
    "Moisture and density are most of why the same recipe drifts between bags.",
    "Moisture and density before the charge, color and grind after the drop.",
    "Moisture and density on the bag you are about to roast, which are the two figures that set the charge.",
    "Moisture and density on the bag you are about to roast, which help you set the charge temperature.",
    "Moisture and density, on a bench or beside a bag.",
    "Moisture and density, the figures a buyer asks for first, at a price that makes sense for a mill.",
    "Moisture and density, the first two figures a buyer asks for, at a price that makes sense for a mill.",
    "Moisture and water activity on the bed, not a fortnight later in somebody else's lab.",
    "Moisture and water activity, before the lot ships.",
    "Moisture ±0.1% · density ±0.5 g/L · color ±0.1",
    "Moisture ±0.1% · density ±0.5 g/L · color ±0.1 Agtron",
    "Moisture ±0.1% · water activity ±0.005 · density ±0.5 g/L",
    "Moisture ±0.2% · density ±1 g/L · 2 s",
    "Moisture, density and size before the charge; roast color and roast delta after the drop.",
    "Moisture, density and size, measured rather than estimated.",
    "Moisture, density and water activity before the roast.",
    "Moisture, water activity and density on a competition coffee, before you buy the only bag of it you will get.",
    "Moisture, water activity and density on arrival, before it goes into a contract.",
    "Moisture, water activity and density on arrival, before the lot goes into a contract, saved to the lot.",
    "Moisture, water activity and density, saved to the coffee, not in a notebook that leaves with someone.",
    "Moisture, water activity, density and size — and the spares that keep them working.",
    "Moisture, water activity, density — filed against the coffee, not a notebook that leaves with someone.",
    "Moisture, water activity, density, screen size",
    "Moisture, water activity, density, screen size and color distribution, the attributes CVA describes, with the method kept beside each figure.",
    "Moisture, water activity, density, screen size and color distribution, the attributes CVA describes.",
    "Moisture, water activity, density, screen size and color from one sample, at the patio or on arrival.",
    "Moisture, water activity, density, screen size and color distribution — the ones CVA asks you to report.",
    "Moisture, water activity, density, size and color from one sample, so the coffee you compete on is described by your own measurements.",
    "Moment",
    "Moment stands over the cup and measures the coffee as it cools, showing the flavors to expect at each temperature. Profiles are written in the Flavor Thermometer in CoffeeOS by a roaster, a barista or you, so you taste the same coffee against the same notes every time.",
    "Most recently added.",
    "New arrivals",
    "No account needed to use the Tools. Sign in when you want the records on more than one phone.",
    "Not your instrument. It is the one behind the spec sheet your roaster can send you.",
    "Omix Plus complete guide",
    "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample of green, parchment, dried cherry or roasted coffee. Water activity is measured by cooling a mirror until dew forms on it, to ±0.005, in about 30 seconds. Every figure goes to the lot's page in CoffeeOS.",
    "Omix Plus measures moisture, water activity, density, screen size and roast color from one sample. Before the roast, density and moisture help you set the charge temperature. After the drop, it gives the final color with the spread of every reading, not just an average. Everything goes to the bean's page in CoffeeOS.",
    "Omni complete guide",
    "Omni measures roast color from 1 to 150 Agtron to ±0.1, on whole beans and on grounds, from about five grams. The Roast Color Analyzer in CoffeeOS puts the two side by side and shows the roast delta between them, which tells you whether the inside of the bean developed as far as the outside. The same instrument measures grind size, which the Brew page covers.",
    "Omni measures the particle size distribution of a few grams of grounds, and the roast color of the beans you are brewing. The Particle Analysis Coaster measures the grind with your phone's camera. Either way the Particle Analyzer in CoffeeOS shows D10, D50, D90 and the spread, so a setting means a measured grind size and a change in the cup can be traced to the grinder or the roast.",
    "OmniFlux hangs outside the roaster window, no modification to the roaster, and tracks the color of the beans through the roast, ±1 Agtron from 20 to 60 cm. Set a target color and it alerts you when the roast gets there. Yellowing, first crack and second crack are marked for you. Off the stand it measures whole beans and grounds to ±0.1 Agtron.",
    "On the day you get one attempt.",
    "One Agtron value in about three seconds, so you will actually check between practice roasts.",
    "One Agtron value in under two seconds, which is the check you will actually run between practice roasts.",
    "One attempt on the day.",
    "One instrument covers most of a green grading module.",
    "One instrument for the green measurements and the roast color distribution.",
    "One method at both ends, so a disagreement has somewhere to land.",
    "One method for the green physicals and the roasted color spread.",
    "One number, no spread",
    "One per bench, without the Omni budget.",
    "One place to check what you were told.",
    "Open a bean record on arrival and put your own numbers beside the seller's.",
    "Open a bean record on arrival and put your own readings beside the seller's.",
    "Particle Analyzer course",
    "Particle Analyzer reports D10, D50, D90 and the standard deviation — the shape, not just the middle.",
    "Particle distribution from a few grams of grounds.",
    "Particle size",
    "Plug-and-play smoke removal for roasters up to 1 kg. Breaks roasting smoke down into carbon dioxide and water vapor.",
    "Profile the burrs once, and every setting after that means something you can carry between machines.",
    "R2 Extract complete guide",
    "R2 Extract measures TDS from one drop in about four seconds, 0 to 30%, ±0.02%. Send it to the Brew Controller and the brew is plotted on the chart with its extraction calculated, so you can see what a change to grind, dose or water did.",
    "R2 PP measures the sugar in cherry juice as degrees Brix, so picking and sorting ripeness is a reading, not a color judgment. One unit covers one range: 0 to 35, 0 to 55 or 0 to 95 °Brix.",
    "Reads roast color through the drum window while the roast is running.",
    "Record the recipe once. Anyone on the bar can follow it, and every brew is saved to the bean it was made with.",
    "Record your best recipe so anyone on your team can brew it the same way.",
    "Refractometers",
    "Refurbished",
    "Roast Color Analyzer takes whole bean and grounds from one sample and gives you the delta between them.",
    "Roast Color Analyzer, on five grams. It is the measurement that tells you whether the batch developed.",
    "Roast by Color with a live color curve, measure whole beans and grounds after the drop, and every batch is saved to the bean it came from.",
    "Roast by Color. Measures roast color through the roaster window during the roast and draws it as a curve.",
    "Roast by color with a live color curve, and remove the smoke with AirWave.",
    "Roast by measurement, not by ear.",
    "Roast color",
    "Roast color and grind size from one instrument, so you can see which one changed the cup.",
    "Roast color and grind size from one instrument.",
    "Roast color and its spread, measured instead of argued about.",
    "Roast color and the roast delta, saved to the lot and sent with the offer.",
    "Roast color and the roast delta, saved to the lot.",
    "Roast color from 1 to 150 Agtron to ±0.1, and grind size distribution, from about five grams.",
    "Roast color meters",
    "Roast color",
    "Roast color and its spread, measured instead of argued about.",
    "Roast color meters",
    "Roast color moves more espressos than the grinder gets blamed for.",
    "Roast color without a lab bench.",
    "Roast in a flat, at any hour, without the smoke.",
    "Roast in an apartment, at any hour, without the smoke.",
    "Roast in the café without smoking out the street.",
    "Roast indoors at whatever hour you practice, without the smoke.",
    "Roast indoors without running a duct outside, and let the fan follow the roast once OmniFlux is paired.",
    "Roast indoors without running a duct outside.",
    "Roast indoors without the ducting that otherwise decides which unit you can rent.",
    "Roast the competition coffee the same way every time.",
    "Roast the next batch better than the last.",
    "Roast the same lot the same way twice.",
    "Roast the same lot twice.",
    "Roasters to 1 kg · 99.9% purification · pairs with OmniFlux",
    "Roasting indoors",
    "Roasting indoors, at the hours practice actually happens, without the room paying for it.",
    "Roasting on site, in a space that has neighbours.",
    "Same dose, same ratio, same grind. Then the difference is the coffee, and every cup is saved to the lot it came from.",
    "Same dose, same ratio, same grind. Then the difference is the coffee.",
    "Sample roast in the sample room, without an extraction hood.",
    "Sample roasting where the sample room is, not where the extraction is.",
    "Sample roasting where the samples are.",
    "Scale + refractometer",
    "Scale and refractometer for one purchase order.",
    "Scale and refractometer together, which costs less than buying them a year apart.",
    "Scale and refractometer, bought as the job rather than as parts.",
    "Scales",
    "See every screen before you buy",
    "See how each student brewed, not just the cup.",
    "See how your own coffee roasts.",
    "See the color move as the roast runs, and drop when it reaches your target.",
    "Send a brew report too.",
    "Send a brew report with the offer.",
    "Send a roast report with the sample.",
    "Send a sample roast with its color measured, not only a tasting note.",
    "Send buyers a sample roast with its color measured, not only a tasting note.",
    "Setting 40 here is not setting 40 there.",
    "Show buyers how it roasts.",
    "Show that the grinder drifted instead of arguing about whether it did.",
    "Single-point color. If you need the distribution, this is not the instrument.",
    "So the routine you take on stage is the one the record says worked, not the one you remember working.",
    "Stands over the cup and measures it as it cools, showing what to taste at each temperature.",
    "Start a bean record for the lot, then hang the moisture and water activity readings on it.",
    "Start with extraction and grind. The green side is here when you want it.",
    "Start with the Grinder Manager. Once your grinder has a profile, every setting means a measured grind size.",
    "Start with the Roast Color Analyzer. Five grams of grounds show you whether the batch developed.",
    "Storage life, answered before you commit to a large lot.",
    "Students pour on the scale, read the TDS, and see the brew plotted on the chart. Every session is saved and shared with you in CoffeeOS.",
    "Students record their work and share it with you in CoffeeOS, on hardware they can buy themselves.",
    "Students roast, measure color before and after, and hand in a session a class can compare.",
    "TDS 0 to 30% · ±0.02% · ~4 s",
    "TDS 0–30% · ±0.02% · ~4 s",
    "TDS by refractometer, dose and yield by scale, the arithmetic done for you.",
    "TDS by refractometer, dose and yield by scale, with the extraction calculated for you and the method kept with the figure.",
    "TDS, and what to do with it",
    "Taking control of your brew",
    "Taste what the roast did.",
    "Taste your coffee the way a buyer will.",
    "Taste your own lot the way the person buying it will.",
    "Tasting",
    "Teach extraction as it happens.",
    "Tell the people drinking your coffee what to taste, and read back which stages they liked.",
    "The Brew Controller shows extraction as it changes, which is the hard part to teach on a slide.",
    "The bench a routine is built on.",
    "The brewing pair for the sample room.",
    "The cheapest way to know the roast color of a bag.",
    "The figure behind the shelf-life claim on a bag.",
    "The finished side of the coffee you sold, measured the same way at both ends.",
    "The finished side of the coffee you sold.",
    "The first measurement in the chain, and affordable enough for a field trip.",
    "The first measurement in the chain, cheap enough for a field trip.",
    "The five green measurements",
    "The green numbers behind the bag are what explain an espresso that keeps moving.",
    "The grind check and the color check in one box.",
    "The grind check and the color check in one instrument.",
    "The grind check and the color check in one box.",
    "The grind under a sample brew, held still, so the cup is the lot and not the setting.",
    "The instrument behind the moisture and density on a green spec sheet.",
    "The instrument behind the moisture and density your roaster quotes to you.",
    "The instrument behind the moisture figure on a green spec sheet.",
    "The instrument that follows the lot from the patio into the drum.",
    "The instrument your roaster uses to check green coffee. Ask for the readings when you ask for the beans.",
    "The instruments a routine is built on.",
    "The instruments either side of the roast.",
    "The instruments that check a lot before it ships.",
    "The loop is the lesson. Each step is a thing a student does, and the last one is what they hand in.",
    "The loop is what makes a recipe survive a staff change: measure, keep it on the bean, open it again tomorrow.",
    "The loop is what makes a roast repeatable: measure, keep it on the bean, compare the next one to it.",
    "The lot keeps its own record.",
    "The measurement a production roastery takes, on a kitchen counter, with each batch logged in CoffeeOS.",
    "The measurement a production roastery takes, on a kitchen counter.",
    "The method, kept with the number.",
    "The most affordable way to know the roast color of a bag.",
    "The numbers on arrival, held next to the numbers you were quoted.",
    "The other half of extraction yield.",
    "The parts that go missing first.",
    "The physicals that set a charge temperature.",
    "The physicals, recorded rather than estimated.",
    "The reading that happens before any of the others.",
    "The reference method, in a single-purpose instrument.",
    "The roast has to land in the same place every time.",
    "The same color measurement on every offer you cup, saved to the lot it belongs to.",
    "The same color method on every offer you cup.",
    "The same color method on every offer you cup.",
    "The same cup, every shift.",
    "The same instrument at the patio and at the roaster, so the readings match.",
    "The same instruments at origin and on arrival, so a difference is in the coffee, not the method.",
    "The same method and the same readings your customers are using.",
    "The same number at origin and on arrival.",
    "The same reading at both ends.",
    "The same reading at origin and on arrival.",
    "The same roast on the day.",
    "The scale under a sample brew.",
    "The trace behind the bag on your shelf.",
    "The two instruments a bar actually uses, and nothing it does not.",
    "The two instruments a routine is built on, bought as the job rather than as parts.",
    "The two numbers a buyer asks for first, at a price a mill can own outright.",
    "To 1 kg · 99.9% purification",
    "Total dissolved solids in about four seconds, 0–30%, ±0.02%.",
    "Try Omix",
    "Two measurements for one purchase.",
    "Understanding color measurement",
    "Up to five stages · 200 profiles",
    "Use the Particle Analyzer to see D10, D50, D90 and the standard deviation: the shape of the grind, not just the middle.",
    "Walk the instrument before you buy it",
    "Watch development happen.",
    "Watch the color curve and adjust your drop time while there is still time, instead of finding out after the drop.",
    "Watch the color curve during the roast instead of finding out after the drop, and match a production batch to the sample roast.",
    "Watch the color curve during the roast instead of finding out after the drop.",
    "Watch the color arrive and move the drop while there is still time, instead of learning where it landed afterwards.",
    "Water activity",
    "Water activity alone, cheap enough for a second unit at origin.",
    "Water activity and moisture, side by side, so the difference stops being abstract.",
    "Water activity by chilled mirror, plus moisture, density, screen size and color distribution, from one sample.",
    "Water activity by the dew point method, so the figure holds up in a report.",
    "Water activity by the most precise method there is, so the figure holds up in a report.",
    "Water activity is measured by cooling a mirror until dew forms on it, the most precise method there is, so the figure holds up in a report.",
    "Water activity on its own, by the same chilled-mirror method.",
    "Water activity ±0.005",
    "Water activity ±0.005, chilled-mirror method",
    "Water activity ±0.02, about 2 minutes",
    "Water activity, chilled mirror",
    "Water activity, so a lot bought months before the competition is still the lot you tasted.",
    "Weight and time on one screen, so the class watches one thing.",
    "What a bar actually uses, and what it does not.",
    "What most people buy first.",
    "What went in and what came out, off one sample, so a batch that behaved differently can be explained rather than guessed at.",
    "What you measured on arrival, next to what you were quoted.",
    "When the moisture or density of the green changes between bags, the shot changes with it. Ask your roaster for the readings.",
    "When the roast color of a bag changes, the shot changes. Measure it, and ask your roaster for their curve.",
    "When the roast color of a bag changes, the shot changes. The grinder usually gets the blame.",
    "Where a lot's story starts: the sugar in the cherry.",
    "Whether the change you made actually changed the cup, measured before it goes cold.",
    "Whether the roast is soluble, not merely dark.",
    "Whole bean against ground on one instrument, and the delta between them — the figure that tells you whether the inside caught up with the outside.",
    "Whole bean and ground on one scale, with the spread reported.",
    "Whole bean and grounds on one scale, with the spread of each.",
    "Whole beans and grounds on one scale, with the spread of each and the method kept with the figure.",
    "Why bags taste different.",
    "Why the espresso keeps drifting.",
    "Why the same coffee tastes different from bag to bag.",
    "Why today's cup was better.",
    "Why two bags of the same coffee taste different.",
    "Why two meters disagree",
    "Work a student can hand in.",
    "Work that ends in a file.",
    "Yesterday's brew, ready to run again.",
    "Yesterday's brew, recorded, with the dose and grind that produced it.",
    "Yesterday's brew, written down, with the dose and grind that produced it.",
    "You watch the color arrive instead of learning where it landed after the drop.",
    "Your grinder, in numbers.",
    "Your notes, on the cup someone is drinking.",
    "accessories",
    "analysis-equipment",
    "analyzers",
    "best-sellers",
    "brew",
    "brew-control-system",
    "brew-control-systems",
    "brewing",
    "bundles",
    "cases-and-insulators",
    "coffee-brewing-scales-accessories",
    "coffee-density-analyzers-accessories",
    "coffee-moisture-analyzers-accessories",
    "coffee-water-activity-analyzers-accessories",
    "color-meters",
    "difluid-airwave",
    "difluid-moment-coffee-flavor-reference-thermometer",
    "extras-and-add-ons",
    "featured",
    "field",
    "gift-cards",
    "gifts",
    "green-coffee-analyzers",
    "insights",
    "moment",
    "new-arrivals",
    "origin",
    "particle-analyzers-and-accessories",
    "r-series",
    "record",
    "refurbished",
    "roasting",
    "share",
    "t-beanmanager",
    "t-brewcontroller",
    "t-brewrecorder",
    "t-grindermanager",
    "t-particle",
    "t-roastcolor",
    "tools"
  ];
  var RETIRED_SET = {};
  for (var ri = 0; ri < RETIRED.length; ri++) RETIRED_SET[RETIRED[ri]] = true;
  for (var hi = 0; hi < HOME.retired.length; hi++) RETIRED_SET[HOME.retired[hi]] = true;

  /* Walk a parsed data-os-copy block and blank every retired line. */
  function unretire(o) {
    if (!o || typeof o !== "object") return o;
    for (var k in o) {
      if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
      var v = o[k];
      if (typeof v === "string") { if (RETIRED_SET[v]) o[k] = ""; }
      else if (v && typeof v === "object") unretire(v);
    }
    return o;
  }

  /* Pages an app renders rather than a template. One line each, put
     under the app's own heading by os.js. */
  var APPS = {
    track: "Enter the order number from your confirmation email, or the tracking number from your shipping email."
  };

  STAGES.systems = SYSTEMS;

  return {
    FAMILY: FAMILY, HOME: HOME, STAGES: STAGES, SYSTEMS: SYSTEMS,
    COFFEEOS: COFFEEOS, SHOP: SHOP, APPS: APPS, pick: pick,
    RETIRED: RETIRED, unretire: unretire
  };
})();

/* Language decks.
   English above is the source. A language file, assets/os-content.<lang>.js,
   loads right after this one (snippets/os-head.liquid lists which exist) and
   calls OS_DECK_APPLY with its translations:
     t: { "<fingerprint of the English>": "translation" }
     p: { "<path>": ["<fingerprint>", "translation"] }   only where one
        English line needs a different translation in one place
   Each string here is looked up by the fingerprint of its English, so a line
   whose English changed shows English until it is translated again, and
   nothing ever shows blank. Identifiers, slugs, image names, links and
   positions are skipped. The same rules live in the sweep script
   (i18n/deck.js in the working folder); keep the two in step. */
window.OS_DECK = {
  SKIP: { img: 1, slug: 1, url: 1, href: 1, icon: 1, id: 1, tool: 1, gear: 1, shot: 1,
          pos: 1, part: 1, alsoSlug: 1, src: 1, key: 1, video: 1, color: 1 },
  text: function (k, v) {
    if (!v || this.SKIP[k]) return false;
    if (/^[a-z0-9][a-z0-9-]*$/.test(v)) return false;
    if (/^(\/|https?:|#|mailto:)/.test(v)) return false;
    if (/^-?\d+(\.\d+)?%( -?\d+(\.\d+)?%)?$/.test(v)) return false;
    return true;
  },
  fp: function (s) {
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return ("0000000" + h.toString(16)).slice(-8);
  },
  walk: function (root, fn) {
    var seen = [];
    (function go(o, path) {
      for (var k in o) {
        if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
        /* RETIRED lists old English lines to blank out of theme-editor
           copy; it has to stay English for that to work. */
        if (k === "RETIRED" || k === "retired") continue;
        var v = o[k], p = path ? path + "." + k : k;
        if (typeof v === "string") fn(o, k, v, p);
        else if (v && typeof v === "object") {
          if (seen.indexOf(v) > -1) continue;
          seen.push(v);
          go(v, p);
        }
      }
    })(root, "");
  }
};
window.OS_DECK_APPLY = function (lang, d) {
  var C = window.OS_CONTENT, X = window.OS_DECK;
  if (!C || !d) return;
  var t = d.t || {}, p = d.p || {}, n = 0;
  X.walk(C, function (o, k, v, path) {
    var key = /^\d+$/.test(k) ? "" : k;
    if (!X.text(key, v)) return;
    var f = X.fp(v), q = p[path];
    var tr = q && q[0] === f ? q[1] : t[f];
    if (typeof tr === "string" && tr) { o[k] = tr; n++; }
  });
  X.lang = lang;
  X.applied = n;
};
